import { useState, useEffect, useCallback } from "react";

const ANTHROPIC_API = "/api/blend";

// ─────────────────────────────────────────────────────────────────
// INGREDIENT LIBRARY — edit this table to update all ingredient
// behaviour across the app. Columns:
//   price      — extra cost per dose added via chat (£)
//   maxDoses   — 1 = single add only; 2/3 = Standard/Enriched/Deeply Enriched shown
//   scent      — shown to customer when discussing ingredient ("None" for odourless)
//   allergens  — array of allergen flags (shown as cautions)
//   premium    — true = "Elevate" language used; false = straightforward add
// ─────────────────────────────────────────────────────────────────
const INGREDIENTS = {
  // ── Essential oils — standard tier ──
  "Lavender":          { price: 0,  maxDoses: 3, scent: "Soft floral, herbaceous, clean",         allergens: [],           premium: false },
  "Roman Chamomile":   { price: 0,  maxDoses: 3, scent: "Gentle apple-like floral, warm, sweet",  allergens: [],           premium: false },
  "Cedarwood":         { price: 0,  maxDoses: 3, scent: "Warm woody, dry, gently smoky",           allergens: [],           premium: false },
  "Bergamot":          { price: 0,  maxDoses: 3, scent: "Bright citrus-floral, uplifting",         allergens: [],           premium: false },
  "Rosemary":          { price: 0,  maxDoses: 3, scent: "Sharp herbal, fresh, slightly medicinal", allergens: [],           premium: false },
  "Lemon":             { price: 0,  maxDoses: 3, scent: "Clean bright citrus, light, fresh",       allergens: [],           premium: false },
  "Peppermint":        { price: 0,  maxDoses: 2, scent: "Cool, sharp mint — very dominant",        allergens: [],           premium: false },
  "Marjoram":          { price: 0,  maxDoses: 3, scent: "Warm herbal, slightly spicy, earthy",     allergens: [],           premium: false },
  "Vetiver":           { price: 0,  maxDoses: 2, scent: "Deep earthy, smoky, grounding — strong",  allergens: [],           premium: false },
  "Rose Geranium":     { price: 0,  maxDoses: 3, scent: "Rosy floral with a green, leafy edge",    allergens: [],           premium: false },
  "Clary Sage":        { price: 0,  maxDoses: 2, scent: "Warm, slightly nutty, herbaceous",        allergens: [],           premium: false },
  "Vanilla Absolute":  { price: 1,  maxDoses: 2, scent: "Rich, warm, sweet vanilla",               allergens: [],           premium: false },
  // ── Essential oils — premium tier ──
  "Frankincense":      { price: 2,  maxDoses: 3, scent: "Resinous, woody, meditative — ancient",   allergens: [],           premium: true  },
  "Sandalwood":        { price: 2.5,maxDoses: 3, scent: "Creamy, warm woody, soft and lingering",  allergens: [],           premium: true  },
  "Neroli":            { price: 3,  maxDoses: 2, scent: "Delicate orange blossom, fresh floral",   allergens: [],           premium: true  },
  // ── Essential oils — ultra-premium tier ──
  "Rose Otto":         { price: 6,  maxDoses: 1, scent: "Deep, velvety, true rose — extraordinary", allergens: [],          premium: true  },
  "Jasmine Absolute":  { price: 5,  maxDoses: 1, scent: "Rich, heady floral — intensely feminine",  allergens: [],          premium: true  },
  // ── Herbal extracts ──
  "Valerian":          { price: 1.5,maxDoses: 2, scent: "Earthy, slightly musty — subtle in blend", allergens: [],          premium: false },
  "Passionflower":     { price: 1.5,maxDoses: 2, scent: "Very mild, slightly grassy",               allergens: [],          premium: false },
  "Hops":              { price: 1,  maxDoses: 2, scent: "Earthy, mildly bitter herbal",             allergens: [],          premium: false },
  "Lemon Balm":        { price: 1,  maxDoses: 2, scent: "Fresh lemon-herb, gentle and light",       allergens: [],          premium: false },
  "Ashwagandha":       { price: 1.5,maxDoses: 2, scent: "Earthy, slightly woody — subtle",          allergens: [],          premium: false },
  "Rhodiola":          { price: 2,  maxDoses: 2, scent: "Mild rose-like herbal",                    allergens: [],          premium: false },
  "Holy Basil":        { price: 1.5,maxDoses: 2, scent: "Warm spiced herbal, slightly clove-like",  allergens: [],          premium: false },
  "Reishi":            { price: 2,  maxDoses: 2, scent: "Earthy, mushroom-like — very subtle",      allergens: [],          premium: false },
  "Vitex":             { price: 2,  maxDoses: 2, scent: "Mild peppery-herbal",                      allergens: [],          premium: false },
  "Black Cohosh":      { price: 2,  maxDoses: 2, scent: "Faintly sweet, earthy",                    allergens: [],          premium: false },
  // ── Active compounds ──
  "Magnesium Chloride":{ price: 1.5,maxDoses: 2, scent: "None — completely odourless in blend",     allergens: [],          premium: false },
  "Caffeine":          { price: 1,  maxDoses: 1, scent: "None — odourless",                         allergens: [],          premium: false },
  "Vitamin E":         { price: 0,  maxDoses: 1, scent: "None — odourless",                         allergens: [],          premium: false },
  "Vitamin D3":        { price: 0,  maxDoses: 1, scent: "Faint coconut note from the carrier",      allergens: [],          premium: false },
  // ── Functional oils ──
  "Rosehip":           { price: 3,  maxDoses: 2, scent: "Light, slightly fruity — very subtle",     allergens: [],          premium: false },
  "Evening Primrose":  { price: 3,  maxDoses: 2, scent: "Very mild, almost imperceptible",          allergens: [],          premium: false },
  "Hemp Seed":         { price: 2,  maxDoses: 2, scent: "Faintly nutty, earthy — very subtle",      allergens: [],          premium: false },
};

// Dose level labels (used when maxDoses > 1)
const DOSE_LABELS = ["Standard", "Enriched", "Deeply Enriched"];

// Helper to get price for an ingredient
const getPrice = (name) => INGREDIENTS[name]?.price ?? 0;
const getMaxDoses = (name) => INGREDIENTS[name]?.maxDoses ?? 1;

const KNOWLEDGE = `
You are the My Alchemy personalization AI. You create personalized topical wellness blends combining essential oils, herbal extracts, and functional base oils.

CRITICAL LANGUAGE RULES:
- NEVER say: treats, cures, heals, remedy, clinically proven, will help, relieves (medical context)
- ALWAYS say: "many people use this for...", "traditionally associated with...", "some research suggests...", "individual responses vary"
- This is a cosmetic product for topical use. You provide honest information; the customer makes choices.

PRODUCT: 10ml roller-ball bottle. Max 4-5 essential oils per blend. Always suggest a balanced scent structure.

SCENT NOTES:
- Top notes (1-2hrs): Bergamot, Lemon, Peppermint, Eucalyptus, Basil
- Middle notes (2-4hrs): Lavender, Roman Chamomile, Rose Geranium, Clary Sage, Ylang Ylang, Marjoram, Neroli, Jasmine
- Base notes (6-10hrs): Vetiver, Sandalwood, Frankincense, Cedarwood, Patchouli, Rose Otto

SCENT FAMILIES: Floral, Citrus, Woody/Earthy, Resinous/Warm, Herbal/Fresh, Minty

SCENT CLASHES TO AVOID:
- Peppermint + Chamomile (mint destroys soft floral)
- Ylang Ylang >5% of EO portion causes headaches — accent only, always flag
- Eucalyptus in floral blends (clinical smell)
- Heavy patchouli + citrus at equal proportions

GOAL INGREDIENT POOLS:
SLEEP: Lavender, Roman Chamomile, Vetiver, Cedarwood, Marjoram, Sandalwood, Bergamot, Frankincense, Clary Sage + extracts: Valerian, Passionflower, Hops, Lemon Balm
STRESS: Bergamot, Frankincense, Rose Geranium, Lavender, Clary Sage, Sandalwood, Neroli, Ylang Ylang (accent) + adaptogens: Ashwagandha, Rhodiola, Lemon Balm, Holy Basil
FOCUS: Rosemary, Peppermint (accent), Lemon, Eucalyptus, Bergamot, Basil, Cedarwood + Rhodiola, Caffeine
BALANCE: Clary Sage, Rose Geranium, Frankincense, Bergamot, Lavender, Neroli, Sandalwood, Rose Otto, Ylang Ylang (accent) + Vitex, Evening Primrose, Ashwagandha, Black Cohosh

AGE WEIGHTING:
- Under 25: lighter, fresher profiles; avoid anything with pregnancy cautions in recommendations
- 25-40: full range applicable
- 41-55: weight towards BALANCE ingredients (Clary Sage, Vitex, Evening Primrose) even in sleep/stress blends if female
- 55+: similar to 41-55, gentle grounding profiles, Frankincense, Sandalwood

GENDER WEIGHTING:
- Female + 41-55 or 55+: consider hormonal support ingredients (Vitex, Clary Sage, Evening Primrose, Rose Geranium) across all goals
- Male: Cedarwood, Vetiver, Frankincense, Rosemary tend to resonate; lighter florals
- Non-binary/prefer not to say: full range, let scent preference lead

PREMIUM PRICING TIERS:
- Standard (£28-32): Lavender, Chamomile, Cedarwood, Bergamot, Rosemary, Lemon, Peppermint, Marjoram, Vetiver, Rose Geranium, common herbals
- Premium (£38-45): Frankincense or Sandalwood as a primary oil, or Neroli as secondary
- Ultra-premium (£55-65): Rose Otto, Jasmine Absolute, or Neroli+Sandalwood combination

PEPPERMINT: Standard ingredient. Include in FOCUS blends as top note accent. Available as cooling accent in STRESS blends. Flag strength — recommend as accent not primary.

UPGRADE LOGIC — always suggest exactly one upgrade. Lead with the ingredient name as the headline:
- Standard blend → suggest Frankincense, Sandalwood, or Neroli as an upgrade
- Any blend → Rose Otto is always the pinnacle upgrade option
- Rose Geranium is a good standard ingredient, NOT a premium upgrade trigger
- For Rose Otto specifically, always include the petal fact: it takes approximately 800 rose petals to produce the tiny amount of Rose Otto we add to a single bottle. Rose Otto is considered one of the most luxurious and expensive essential oils in the world. This context belongs in the upgrade description.

ALWAYS suggest one premium upgrade. The "upgrade" JSON object must have "ingredient" as the prominent headline name.

SENSITIVITIES:
- Pregnant: REMOVE Clary Sage, Rosemary, avoid high camphor oils. Flag clearly.
- Nut allergy: 100% jojoba base, remove sweet almond. Note this.
- Vegan: All standard ingredients are vegan. No emu oil (we don't stock anyway).

HONESTY ABOUT UNCERTAIN INGREDIENTS:
- CBD: Not currently stocked. Better systemic effect via oral route. Can discuss honestly.
- Magnesium Chloride (topical): This IS stocked and CAN be included. Approximately 40% of people report noticeable benefit for sleep and relaxation when applied topically; evidence is mixed but real. Magnesium chloride is the correct form for topical application (not glycinate, which is the oral form). Many people find it helps with physical tension, sleep preparation, and muscle relaxation. Include honestly with appropriate language. Standard tier, £3 per dose.
- GABA topical: Unproven mechanism. We don't include it.
- L-Theanine topical: Better taken orally. We don't include it.
- Melatonin: Prescription medicine in the UK. We cannot include it. Suggest alternatives.

DIY PATH: If customer selected DIY mode, include proportions (ml) for each ingredient so they can make it themselves. Still recommend purchasing for convenience and cost comparison.

A complete blend: 1 base note + 1-2 middle notes + 1 top note + optional herbal extract + Vitamin E (always included, standard).

CHAT ADVISOR ROLE:
When acting as the chat advisor (not blend generation), you can:
- Answer general wellness questions about sleep, stress, focus, hormonal health
- Explain how ingredients work, what the evidence says (honestly)
- Add ingredients to the current blend (up to 4 total doses per ingredient)
- Suggest premium upgrades with pricing
- Handle unknown requests gracefully: if something isn't in our library, acknowledge it, explain why we don't include it, and invite the customer to suggest it for future blends via contact
- Discuss wider sleep science, stress physiology, aromatherapy research — draw on full knowledge
- Always stay in MY ALCHEMY brand voice: honest, warm, informed, never overclaiming
`;

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400;1,500&family=DM+Sans:wght@200;300;400&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --gold: #b8935a;
    --gold-light: #d4ad7a;
    --cream: #f2ebe0;
    --warm-white: #faf7f2;
    --ink: #1a1714;
    --ink-soft: #2e2924;
    --muted: rgba(26,23,20,0.45);
    --faint: rgba(26,23,20,0.15);
    --border: rgba(26,23,20,0.1);
  }

  body { background: var(--warm-white); color: var(--ink); font-family: 'DM Sans', sans-serif; font-weight: 300; }

  .app {
    min-height: 100vh;
    background: var(--warm-white);
    position: relative;
  }

  /* Decorative corner lines */
  .app::before {
    content: '';
    position: fixed;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--gold), transparent);
    opacity: 0.6;
    z-index: 10;
  }

  .grain {
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
    opacity: 0.4;
  }

  .container {
    max-width: 640px;
    margin: 0 auto;
    padding: 48px 28px 100px;
    position: relative;
    z-index: 1;
  }

  /* Header */
  .site-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 72px;
    padding-top: 12px;
  }
  .logo {
    font-family: 'Playfair Display', serif;
    font-size: 52px;
    font-weight: 400;
    letter-spacing: 0.02em;
    color: var(--ink);
    line-height: 1;
  }
  .logo span { color: var(--gold); }
  .logo-wrap { display: flex; flex-direction: column; gap: 6px; }
  .logo-strapline {
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    font-weight: 500;
    font-style: normal;
    color: var(--ink);
    letter-spacing: 0.01em;
    margin-top: 4px;
  }
  .price-intro-line { display: none; }
  .mode-toggle {
    font-size: 11px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--muted);
    cursor: pointer;
    background: none;
    border: none;
    font-family: 'DM Sans', sans-serif;
    font-weight: 300;
    transition: color 0.2s;
    padding: 4px 0;
    border-bottom: 1px solid transparent;
  }
  .mode-toggle:hover { color: var(--gold); border-bottom-color: var(--gold); }
  .mode-toggle.active { color: var(--gold); border-bottom-color: var(--gold); }

  /* Progress */
  .progress-wrap { margin-bottom: 56px; }
  .progress-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }
  .progress-step {
    font-size: 10px;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: var(--gold);
  }
  .progress-fraction {
    font-size: 10px;
    letter-spacing: 0.15em;
    color: var(--muted);
  }
  .progress-track {
    height: 1px;
    background: var(--border);
    position: relative;
  }
  .progress-fill {
    position: absolute;
    left: 0; top: 0; height: 100%;
    background: var(--gold);
    transition: width 0.5s cubic-bezier(0.4,0,0.2,1);
  }

  /* Questions */
  .q-wrap { animation: slideUp 0.4s cubic-bezier(0.4,0,0.2,1) both; }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .q-eyebrow {
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold);
    margin-bottom: 14px;
    opacity: 0.8;
  }
  .q-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(24px, 4.5vw, 36px);
    font-weight: 400;
    line-height: 1.25;
    color: var(--ink);
    margin-bottom: 10px;
  }
  .q-title em { font-style: italic; color: var(--gold); }
  .q-sub {
    font-size: 13px;
    color: var(--muted);
    line-height: 1.65;
    margin-bottom: 36px;
    max-width: 480px;
  }

  /* Options */
  .opts { display: grid; gap: 8px; margin-bottom: 28px; }
  .opts.two { grid-template-columns: 1fr 1fr; }
  .opts.one { grid-template-columns: 1fr; }

  .opt {
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 6px;
    padding: 16px 20px;
    cursor: pointer;
    text-align: left;
    transition: all 0.2s ease;
    font-family: 'DM Sans', sans-serif;
    color: var(--ink);
    position: relative;
    overflow: hidden;
  }
  .opt::after {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--gold);
    opacity: 0;
    transition: opacity 0.2s;
  }
  .opt:hover { border-color: var(--gold-light); }
  .opt:hover::after { opacity: 0.04; }
  .opt.sel {
    border-color: var(--gold);
    background: white;
  }
  .opt.sel::after { opacity: 0.06; }
  .opt-check {
    position: absolute;
    top: 14px; right: 16px;
    width: 16px; height: 16px;
    border: 1.5px solid var(--border);
    border-radius: 50%;
    transition: all 0.2s;
    display: flex; align-items: center; justify-content: center;
  }
  .opt.sel .opt-check {
    background: var(--gold);
    border-color: var(--gold);
  }
  .opt.sel .opt-check::after {
    content: '';
    width: 5px; height: 5px;
    border-radius: 50%;
    background: white;
  }
  .opt-label {
    font-size: 14px;
    font-weight: 400;
    color: var(--ink);
    display: block;
    margin-bottom: 2px;
    padding-right: 28px;
    position: relative;
    z-index: 1;
  }
  .opt-sub {
    font-size: 11px;
    color: var(--muted);
    letter-spacing: 0.02em;
    position: relative;
    z-index: 1;
  }

  /* Range / Age slider */
  .range-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 28px; }
  .range-btn {
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 6px;
    padding: 14px 8px;
    cursor: pointer;
    font-family: 'Playfair Display', serif;
    font-size: 15px;
    font-weight: 400;
    color: var(--ink);
    text-align: center;
    transition: all 0.2s;
  }
  .range-btn:hover { border-color: var(--gold-light); }
  .range-btn.sel { border-color: var(--gold); background: rgba(184,147,90,0.05); color: var(--gold); }
  .range-label { font-size: 10px; letter-spacing: 0.1em; color: var(--muted); display: block; margin-top: 3px; font-family: 'DM Sans', sans-serif; }

  /* Text inputs */
  .field-wrap { margin-bottom: 20px; }
  .field-label {
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
    display: block;
    margin-bottom: 8px;
  }
  .field-note { font-size: 10px; color: var(--muted); opacity: 0.6; float: right; }
  .txt-input, .txt-area {
    width: 100%;
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 6px;
    padding: 14px 18px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 300;
    color: var(--ink);
    outline: none;
    transition: border-color 0.2s;
  }
  .txt-input:focus, .txt-area:focus { border-color: var(--gold); }
  .txt-input::placeholder, .txt-area::placeholder { color: rgba(26,23,20,0.25); }
  .txt-area { resize: none; line-height: 1.6; }

  /* Inline chat — replaces freetext box */
  .inline-chat {
    margin-top: 4px;
    padding-top: 20px;
    border-top: 1px solid var(--faint);
    border-radius: 8px;
  }
  .inline-chat-header {
    display: flex; align-items: baseline; gap: 10px;
    margin-bottom: 12px;
  }
  .inline-chat-title {
    font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase;
    color: var(--gold); font-weight: 400;
  }
  .inline-chat-sub {
    font-size: 11px; color: var(--muted); opacity: 0.7;
  }
  .inline-chat-messages {
    display: flex; flex-direction: column; gap: 10px;
    margin-bottom: 12px;
    max-height: 280px;
    overflow-y: auto;
  }
  .inline-chat-input-row {
    display: flex; gap: 8px;
  }
  .inline-chat-input {
    flex: 1;
    border: 1.5px solid var(--border);
    border-radius: 6px;
    padding: 12px 16px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    outline: none;
    transition: border-color 0.2s;
    color: var(--ink);
    background: white;
  }
  .inline-chat-input:focus { border-color: var(--gold); }
  .inline-chat-input::placeholder { color: rgba(26,23,20,0.3); }
  .inline-chat-send {
    background: var(--gold); border: none; border-radius: 6px;
    padding: 12px 18px; cursor: pointer; color: white;
    font-size: 14px; transition: all 0.2s; flex-shrink: 0;
  }
  .inline-chat-send:hover { background: var(--gold-light); }
  .inline-chat-send:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Chat messages */
  .chat-msg { max-width: 88%; font-size: 13px; line-height: 1.55; padding: 10px 14px; border-radius: 10px; }
  .chat-msg.assistant { background: #f5f0e8; color: var(--ink); align-self: flex-start; border-bottom-left-radius: 3px; }
  .chat-msg.user { background: var(--ink); color: var(--cream); align-self: flex-end; border-bottom-right-radius: 3px; margin-left: auto; }
  .chat-msg.thinking { background: #f5f0e8; align-self: flex-start; display: flex; gap: 4px; align-items: center; padding: 12px 16px; }

  /* Chat action buttons */
  .chat-action-row { padding: 6px 0 4px 4px; }
  .chat-add-single {
    background: var(--gold); color: white; border: none;
    border-radius: 5px; padding: 9px 18px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px; letter-spacing: 0.08em;
    cursor: pointer; transition: all 0.2s;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .chat-add-single:hover { background: var(--gold-light); }
  .chat-add-price { opacity: 0.85; font-size: 11px; }
  .chat-added-confirm {
    font-size: 12px; color: #5a8a7a;
    display: flex; align-items: center; gap: 10px;
    padding: 4px 0;
  }
  .chat-remove-btn {
    background: none; border: 1px solid var(--border);
    border-radius: 3px; padding: 3px 10px;
    font-size: 10px; color: var(--muted); cursor: pointer;
    font-family: 'DM Sans', sans-serif; letter-spacing: 0.1em;
    transition: all 0.2s;
  }
  .chat-remove-btn:hover { border-color: var(--ink); color: var(--ink); }
  .chat-dose-label { font-size: 11px; color: var(--muted); margin-bottom: 8px; letter-spacing: 0.05em; }
  .chat-dose-options { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 6px; }
  .chat-dose-option {
    background: white; border: 1.5px solid var(--border);
    border-radius: 5px; padding: 8px 14px;
    font-family: 'DM Sans', sans-serif; cursor: pointer;
    transition: all 0.2s; display: flex; flex-direction: column;
    align-items: center; gap: 3px;
  }
  .chat-dose-option:hover { border-color: var(--gold-light); }
  .chat-dose-option.selected { border-color: var(--gold); background: rgba(184,147,90,0.06); }
  .dose-option-label { font-size: 12px; color: var(--ink); font-weight: 400; }
  .dose-option-price { font-size: 10px; color: var(--gold); }

  /* Freetext wrap — shown on steps 1-2 */
  .freetext-wrap { margin-top: 16px; margin-bottom: 4px; }

  /* Floating support chat — steps 3-8, desktop: fixed right, mobile: inline below form */
  .floating-chat {
    position: fixed;
    top: 90px;
    right: 0;
    width: 320px;
    z-index: 50;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  .floating-chat-toggle {
    background: var(--gold);
    color: white;
    border: none;
    border-radius: 8px 0 0 8px;
    padding: 13px 22px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: background 0.2s, padding 0.2s;
    align-self: flex-end;
    box-shadow: -3px 3px 16px rgba(184,147,90,0.25);
  }
  .floating-chat-toggle:hover { background: var(--gold-light); }
  .floating-chat-toggle.open { background: var(--ink); border-radius: 8px 0 0 0; box-shadow: none; }
  .floating-chat-toggle-icon { font-size: 16px; }
  .floating-chat-panel {
    width: 320px;
    background: white;
    border: 1px solid var(--border);
    border-right: none;
    border-radius: 8px 0 0 8px;
    box-shadow: -4px 8px 32px rgba(0,0,0,0.10);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: slideUp 0.2s ease both;
  }
  .floating-chat-messages {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 280px;
    overflow-y: auto;
    font-size: 13px;
  }
  .floating-chat-input-row {
    display: flex;
    gap: 6px;
    padding: 12px 14px;
    border-top: 1px solid var(--border);
    background: white;
  }
  .floating-chat-input {
    flex: 1;
    border: 1.5px solid var(--border);
    border-radius: 6px;
    padding: 9px 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    outline: none;
    transition: border-color 0.2s;
    color: var(--ink);
    background: white;
  }
  .floating-chat-input:focus { border-color: var(--gold); }
  .floating-chat-input::placeholder { color: rgba(26,23,20,0.3); }
  .floating-chat-send {
    background: var(--gold); border: none; border-radius: 6px;
    padding: 9px 16px; cursor: pointer; color: white;
    font-size: 14px; transition: all 0.2s; flex-shrink: 0;
  }
  .floating-chat-send:hover { background: var(--gold-light); }
  .floating-chat-send:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Mobile: not fixed, sits inline below the form */
  @media (max-width: 900px) {
    .floating-chat {
      position: static;
      width: 100%;
      margin-top: 28px;
      align-items: stretch;
    }
    .floating-chat-toggle {
      border-radius: 8px;
      width: 100%;
      justify-content: center;
      box-shadow: none;
    }
    .floating-chat-toggle.open { border-radius: 8px 8px 0 0; }
    .floating-chat-panel {
      width: 100%;
      border-right: 1px solid var(--border);
      border-radius: 0 0 8px 8px;
    }
  }

  /* Nav */
  .nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 36px;
  }
  .btn-back {
    background: none;
    border: none;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--muted);
    cursor: pointer;
    padding: 0;
    transition: color 0.2s;
  }
  .btn-back:hover { color: var(--ink); }
  .btn-next {
    background: var(--ink);
    color: var(--cream);
    border: none;
    border-radius: 4px;
    padding: 14px 32px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 400;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.2s;
  }
  .btn-next:hover { background: var(--ink-soft); }
  .btn-next:disabled { opacity: 0.25; cursor: not-allowed; }
  .btn-next.gold {
    background: var(--gold);
    color: white;
  }
  .btn-next.gold:hover { background: var(--gold-light); }
  .btn-skip {
    background: none; border: none;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px; letter-spacing: 0.12em;
    color: rgba(26,23,20,0.25);
    cursor: pointer; text-transform: uppercase;
    transition: color 0.2s;
  }
  .btn-skip:hover { color: var(--muted); }

  /* Loading */
  .loading {
    text-align: center;
    padding: 80px 0;
    animation: slideUp 0.5s ease both;
  }
  .loading-eyebrow {
    font-size: 10px; letter-spacing: 0.3em; text-transform: uppercase;
    color: var(--gold); margin-bottom: 20px;
  }
  .loading-title {
    font-family: 'Playfair Display', serif;
    font-size: 34px; font-weight: 400; font-style: italic;
    color: var(--ink); margin-bottom: 10px;
  }
  .loading-line {
    font-size: 12px; color: var(--muted); letter-spacing: 0.15em;
    text-transform: uppercase; margin-bottom: 48px;
    min-height: 20px;
  }
  .loader-dots { display: flex; justify-content: center; gap: 10px; }
  .loader-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--gold);
    animation: blink 1.4s infinite ease-in-out;
  }
  .loader-dot:nth-child(2) { animation-delay: 0.2s; }
  .loader-dot:nth-child(3) { animation-delay: 0.4s; }
  @keyframes blink {
    0%, 80%, 100% { opacity: 0.2; transform: scale(0.7); }
    40% { opacity: 1; transform: scale(1); }
  }

  /* Result */
  .result { animation: slideUp 0.5s ease both; }

  .result-hero {
    padding-bottom: 36px;
    margin-bottom: 36px;
    border-bottom: 1px solid var(--border);
  }
  .result-eyebrow {
    font-size: 10px; letter-spacing: 0.3em; text-transform: uppercase;
    color: var(--gold); margin-bottom: 14px;
  }

  /* Editable name fields */
  .name-edit-wrap { margin-bottom: 6px; }
  .name-display {
    font-family: 'Playfair Display', serif;
    font-size: clamp(28px, 5vw, 44px);
    font-weight: 400;
    line-height: 1.15;
    color: var(--ink);
    cursor: text;
    border-bottom: 2px dashed transparent;
    transition: border-color 0.2s;
    display: inline-block;
    min-width: 100px;
  }
  .name-display:hover { border-bottom-color: var(--gold-light); }
  .name-edit-input {
    font-family: 'Playfair Display', serif;
    font-size: clamp(28px, 5vw, 44px);
    font-weight: 400;
    line-height: 1.15;
    color: var(--ink);
    border: none;
    border-bottom: 2px solid var(--gold);
    outline: none;
    background: transparent;
    width: 100%;
    font-style: italic;
  }
  .name-hint {
    font-size: 10px; color: var(--muted); letter-spacing: 0.1em;
    margin-top: 4px; opacity: 0.6;
  }

  .bring-me-label {
    font-family: 'Playfair Display', serif;
    font-size: clamp(32px, 5.5vw, 48px);
    font-weight: 500;
    color: var(--ink);
    display: block;
    margin-bottom: 4px;
  }
  .bring-me-sub {
    font-family: 'Playfair Display', serif;
    font-size: clamp(18px, 3vw, 24px);
    font-weight: 400;
    font-style: italic;
    color: var(--gold);
    display: block;
    margin-bottom: 16px;
  }

  .gifted-by {
    font-size: 12px; color: var(--muted); letter-spacing: 0.1em;
    font-style: italic; margin-bottom: 20px; margin-top: 8px;
  }

  .scent-tags { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 18px; }
  .stag {
    border: 1px solid rgba(184,147,90,0.35);
    border-radius: 3px;
    padding: 5px 14px;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--gold);
  }

  .section-label {
    font-size: 10px; letter-spacing: 0.25em; text-transform: uppercase;
    color: var(--muted); margin-bottom: 18px;
  }

  .ing-list { margin-bottom: 36px; }
  .ing {
    display: flex; gap: 18px;
    padding: 16px 0;
    border-bottom: 1px solid var(--faint);
  }
  .ing:last-child { border-bottom: none; }
  .ing-dot {
    width: 4px; height: 4px; border-radius: 50%;
    background: var(--gold); margin-top: 8px; flex-shrink: 0;
  }
  .ing-name { font-size: 14px; font-weight: 400; color: var(--ink); margin-bottom: 3px; }
  .ing-note { font-size: 12px; color: var(--muted); line-height: 1.55; }
  .ing-ml { font-size: 10px; color: var(--gold); letter-spacing: 0.1em; margin-top: 3px; font-weight: 400; }

  /* Advisory / upgrade boxes */
  .advisory {
    background: #fdf8f1;
    border: 1px solid rgba(184,147,90,0.2);
    border-left: 3px solid var(--gold);
    border-radius: 0 6px 6px 0;
    padding: 18px 22px;
    margin-bottom: 24px;
    font-size: 13px;
    color: var(--muted);
    line-height: 1.65;
  }
  .advisory strong {
    font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase;
    color: var(--gold); font-weight: 400; display: block; margin-bottom: 6px;
  }

  .upgrade-box {
    background: linear-gradient(135deg, #fdf8f1, #faf5ec);
    border: 1px solid rgba(184,147,90,0.25);
    border-radius: 8px;
    padding: 22px 24px;
    margin-bottom: 28px;
  }
  .upgrade-box strong {
    font-size: 10px; letter-spacing: 0.25em; text-transform: uppercase;
    color: var(--gold); font-weight: 400; display: block; margin-bottom: 8px;
  }
  .upgrade-desc { font-size: 13px; color: var(--muted); line-height: 1.65; margin-bottom: 14px; }
  .upgrade-price {
    font-family: 'Playfair Display', serif;
    font-size: 22px; color: var(--gold); font-weight: 400;
    display: inline-block; margin-right: 12px;
  }
  .upgrade-cta {
    background: none; border: 1px solid var(--gold);
    border-radius: 3px; padding: 8px 20px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase;
    color: var(--gold); cursor: pointer; transition: all 0.2s;
  }
  .upgrade-cta:hover { background: var(--gold); color: white; }

  /* Variations */
  .variations { margin-bottom: 32px; }
  .var-card {
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    padding: 18px 22px;
    margin-bottom: 10px;
  }
  .var-header { display: flex; align-items: baseline; gap: 12px; margin-bottom: 6px; }
  .var-name { font-size: 13px; font-weight: 400; color: var(--ink); }
  .var-discount {
    font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase;
    color: white; background: var(--gold); border-radius: 3px;
    padding: 3px 10px;
  }
  .var-desc { font-size: 12px; color: var(--muted); line-height: 1.55; }

  /* Price row */
  .price-row {
    display: flex; align-items: center; gap: 20px;
    padding: 24px 0;
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    margin-bottom: 28px;
  }
  .price-main {
    font-family: 'Playfair Display', serif;
    font-size: 48px; font-weight: 400; color: var(--ink);
    line-height: 1;
  }
  .price-meta { flex: 1; }
  .price-tier { font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase; color: var(--muted); }
  .price-sub { font-size: 11px; color: rgba(26,23,20,0.3); margin-top: 5px; }

  .ctas { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 40px; }
  .btn-buy {
    flex: 1; min-width: 180px;
    background: var(--ink); color: var(--cream);
    border: none; border-radius: 5px;
    padding: 16px 28px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px; font-weight: 400;
    letter-spacing: 0.2em; text-transform: uppercase;
    cursor: pointer; transition: all 0.2s;
  }
  .btn-buy:hover { background: var(--ink-soft); }
  .btn-buy.gold-btn { background: var(--gold); }
  .btn-buy.gold-btn:hover { background: var(--gold-light); }
  .btn-outline {
    background: white; color: var(--muted);
    border: 1.5px solid var(--border);
    border-radius: 5px;
    padding: 16px 22px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase;
    cursor: pointer; transition: all 0.2s;
  }
  .btn-outline:hover { border-color: var(--ink); color: var(--ink); }

  .diy-notice {
    background: #f5f9f7;
    border: 1px solid rgba(122,158,142,0.2);
    border-radius: 8px;
    padding: 18px 22px;
    margin-bottom: 28px;
    font-size: 13px; color: var(--muted); line-height: 1.65;
  }
  .diy-notice strong {
    font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase;
    color: #5a8a7a; font-weight: 400; display: block; margin-bottom: 6px;
  }

  .disclaimer {
    font-size: 10px; color: rgba(26,23,20,0.25);
    line-height: 1.8; letter-spacing: 0.02em;
    padding-top: 28px;
    border-top: 1px solid var(--faint);
  }

  /* Label preview section */
  .label-section {
    margin-bottom: 40px;
    padding-top: 36px;
    border-top: 1px solid var(--border);
  }
  .label-section-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 10px;
  }

  /* Bottle mockup */
  .bottle-mockup-wrap {
    display: flex;
    align-items: flex-start;
    gap: 40px;
    margin-bottom: 28px;
    flex-wrap: wrap;
  }
  .bottle-svg-wrap {
    flex-shrink: 0;
  }

  /* Actual label preview — PRIMERA LX500 50x30mm at 3.78px/mm = 189x113px */
  .label-preview-wrap {
    flex: 1;
    min-width: 220px;
  }
  .label-preview-title {
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 10px;
  }
  .label-preview {
    width: 250px;
    height: 150px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    overflow: hidden;
  }
  .label-preview::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #b8935a, #d4ad7a);
  }
  .label-top { flex: 1; }
  .label-brand {
    font-family: 'Playfair Display', serif;
    font-size: 7px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #b8935a;
    margin-bottom: 2px;
  }
  .label-goal {
    font-family: 'Playfair Display', serif;
    font-size: 13px;
    font-weight: 500;
    color: #1a1714;
    line-height: 1.1;
    margin-bottom: 1px;
  }
  .label-name {
    font-family: 'Playfair Display', serif;
    font-size: 9px;
    font-style: italic;
    color: #6b6057;
    margin-bottom: 4px;
  }
  .label-gifted {
    font-size: 6px;
    color: #9a8878;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
  }
  .label-tags {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }
  .label-tag {
    font-size: 5.5px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #b8935a;
    border: 0.5px solid rgba(184,147,90,0.4);
    border-radius: 2px;
    padding: 1.5px 5px;
  }
  .label-bottom {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  }
  .label-legal {
    font-size: 5px;
    color: #aaa;
    line-height: 1.4;
    max-width: 150px;
  }
  .label-qr-placeholder {
    width: 30px;
    height: 30px;
    background: #f5f5f5;
    border: 0.5px solid #ddd;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 4.5px;
    color: #bbb;
    text-align: center;
    flex-shrink: 0;
  }

  /* Gift tag */
  .gift-tag-wrap { margin-top: 24px; }
  .gift-toggle {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }
  .gift-toggle-btn {
    background: none;
    border: 1.5px solid var(--border);
    border-radius: 20px;
    padding: 6px 18px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--muted);
    cursor: pointer;
    transition: all 0.2s;
  }
  .gift-toggle-btn.active {
    border-color: var(--gold);
    color: var(--gold);
    background: rgba(184,147,90,0.05);
  }
  .gift-toggle-price {
    font-size: 11px;
    color: var(--muted);
  }

  .gift-tag-preview {
    width: 320px;
    height: 190px;
    background: var(--warm-white);
    border: 1px solid #ddd;
    border-radius: 6px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.07);
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    margin-bottom: 14px;
  }
  .gift-tag-preview::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #b8935a, transparent);
  }
  .gift-tag-brand {
    font-family: 'Playfair Display', serif;
    font-size: 8px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: #b8935a;
    margin-bottom: 8px;
  }
  .gift-tag-message {
    font-family: 'Playfair Display', serif;
    font-size: 12px;
    font-style: italic;
    color: #1a1714;
    line-height: 1.55;
    flex: 1;
    word-break: break-word;
  }
  .gift-tag-footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 10px;
  }
  .gift-tag-from {
    font-size: 8px;
    color: #9a8878;
    letter-spacing: 0.1em;
  }
  .gift-tag-blend {
    font-size: 7px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #b8935a;
  }

  .gift-message-input {
    width: 100%;
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 6px;
    padding: 12px 16px;
    font-family: 'Playfair Display', serif;
    font-style: italic;
    font-size: 13px;
    color: var(--ink);
    resize: none;
    outline: none;
    transition: border-color 0.2s;
    line-height: 1.55;
    margin-bottom: 6px;
  }
  .gift-message-input:focus { border-color: var(--gold); }
  .gift-message-count {
    font-size: 10px;
    color: var(--muted);
    opacity: 0.5;
    text-align: right;
    margin-bottom: 12px;
  }

  .print-btns {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 16px;
  }
  .btn-print {
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 4px;
    padding: 10px 20px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--muted);
    cursor: pointer;
    transition: all 0.2s;
  }
  .btn-print:hover { border-color: var(--ink); color: var(--ink); }
  .btn-print.primary-print {
    background: var(--ink);
    color: var(--cream);
    border-color: var(--ink);
  }
  .btn-print.primary-print:hover { background: var(--ink-soft); }

  .primer-note {
    font-size: 10px;
    color: var(--muted);
    opacity: 0.5;
    margin-top: 10px;
    line-height: 1.6;
  }

  /* Chat advisor */
  .chat-advisor {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
  }
  .chat-toggle {
    width: 52px; height: 52px;
    border-radius: 50%;
    background: var(--ink);
    border: none;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.18);
    transition: all 0.2s;
    color: var(--cream);
  }
  .chat-toggle:hover { background: var(--gold); transform: scale(1.06); }
  .chat-toggle.open { background: var(--gold); }
  .chat-panel {
    width: 320px;
    max-height: 480px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 40px rgba(0,0,0,0.14);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid var(--border);
    animation: slideUp 0.25s ease both;
  }
  .chat-header {
    padding: 14px 18px;
    background: var(--ink);
    color: var(--cream);
    display: flex; align-items: flex-start; justify-content: space-between;
  }
  .chat-header-title { font-family: 'Playfair Display', serif; font-size: 14px; font-weight: 400; }
  .chat-header-sub { font-size: 10px; opacity: 0.6; letter-spacing: 0.1em; margin-top: 2px; }
  .chat-close { background: none; border: none; color: var(--cream); opacity: 0.6; cursor: pointer; font-size: 18px; line-height: 1; padding: 0; transition: opacity 0.2s; }
  .chat-close:hover { opacity: 1; }
  .chat-messages { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
  .chat-msg { max-width: 85%; font-size: 13px; line-height: 1.55; padding: 10px 14px; border-radius: 10px; }
  .chat-msg.assistant { background: #f5f0e8; color: var(--ink); align-self: flex-start; border-bottom-left-radius: 3px; }
  .chat-msg.user { background: var(--ink); color: var(--cream); align-self: flex-end; border-bottom-right-radius: 3px; }
  .chat-msg.thinking { background: #f5f0e8; align-self: flex-start; display: flex; gap: 4px; align-items: center; padding: 12px 16px; }
  .chat-add-btn { display: inline-block; margin-top: 8px; background: var(--gold); color: white; border: none; border-radius: 4px; padding: 6px 14px; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.2s; }
  .chat-add-btn:hover { background: var(--gold-light); }
  .chat-add-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .chat-dose-row { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
  .chat-dose-btn { width: 26px; height: 26px; border-radius: 50%; border: 1.5px solid var(--gold); background: none; color: var(--gold); font-size: 16px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
  .chat-dose-btn:hover { background: var(--gold); color: white; }
  .chat-dose-btn:disabled { opacity: 0.3; cursor: not-allowed; }
  .chat-dose-count { font-size: 13px; color: var(--ink); min-width: 60px; }
  .chat-price-delta { font-size: 12px; color: var(--gold); font-family: 'Playfair Display', serif; margin-top: 4px; }
  .chat-input-row { display: flex; gap: 8px; padding: 12px 14px; border-top: 1px solid var(--border); background: white; }
  .chat-input { flex: 1; border: 1.5px solid var(--border); border-radius: 20px; padding: 8px 14px; font-family: 'DM Sans', sans-serif; font-size: 13px; outline: none; transition: border-color 0.2s; color: var(--ink); }
  .chat-input:focus { border-color: var(--gold); }
  .chat-send { background: var(--gold); border: none; border-radius: 50%; width: 36px; height: 36px; cursor: pointer; display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; flex-shrink: 0; transition: all 0.2s; }
  .chat-send:hover { background: var(--gold-light); }
  .chat-send:disabled { opacity: 0.4; cursor: not-allowed; }
  .price-update-banner { background: linear-gradient(135deg, #fdf8f1, #faf5ec); border: 1px solid rgba(184,147,90,0.3); border-radius: 6px; padding: 10px 16px; margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .price-update-label { font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); }
  .price-update-value { font-family: 'Playfair Display', serif; font-size: 22px; color: var(--gold); }
  @media (max-width: 480px) { .chat-advisor { bottom: 16px; right: 16px; } .chat-panel { width: calc(100vw - 32px); } }

  @media (max-width: 480px) {
    .opts.two { grid-template-columns: 1fr; }
    .range-grid { grid-template-columns: repeat(2, 1fr); }
    .container { padding: 36px 18px 80px; }
    .ctas { flex-direction: column; }
    .bottle-mockup-wrap { flex-direction: column; }
    .gift-tag-preview { width: 100%; height: auto; min-height: 180px; }
  }

  /* Entry screen */
  .entry-wrap { animation: slideUp 0.5s cubic-bezier(0.4,0,0.2,1) both; }
  .entry-eyebrow {
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold);
    margin-bottom: 24px;
  }
  .entry-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(18px, 3.4vw, 30px);
    font-weight: 500;
    line-height: 1.25;
    color: var(--ink);
    margin-bottom: 20px;
  }
  .entry-sub {
    font-size: 21px;
    line-height: 1.7;
    color: var(--gold);
    margin-bottom: 48px;
    font-weight: 300;
  }
  .entry-paths {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 32px;
    background: rgba(26,23,20,0.03);
    border-radius: 12px;
    padding: 20px;
  }
  @media (max-width: 480px) { .entry-paths { grid-template-columns: 1fr; } }
  .entry-path {
    border: 1.5px solid var(--border);
    border-radius: 8px;
    padding: 28px 22px;
    background: white;
    cursor: pointer;
    transition: all 0.25s;
    text-align: left;
    font-family: 'DM Sans', sans-serif;
  }
  .entry-path:hover { border-color: var(--gold); transform: translateY(-2px); box-shadow: 0 8px 24px rgba(184,147,90,0.12); }
  .entry-path-icon { font-size: 24px; margin-bottom: 14px; display: block; }
  .entry-path-title {
    font-family: 'Playfair Display', serif;
    font-size: 19px;
    color: var(--ink);
    margin-bottom: 10px;
    display: block;
  }
  .entry-path-sub { font-size: 13px; color: var(--muted); line-height: 1.65; font-weight: 300; }

  /* QR scan mock screen */
  .qr-screen {
    text-align: center;
    padding: 32px 0;
    animation: slideUp 0.4s ease both;
  }
  .qr-mock {
    width: 180px; height: 180px;
    margin: 28px auto;
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 24px rgba(0,0,0,0.06);
    overflow: hidden;
  }

  /* Amazon review screen */
  .amazon-ingredients {
    margin: 28px 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .amazon-ingredient-card {
    border: 1.5px solid var(--border);
    border-radius: 8px;
    padding: 16px 18px;
    background: white;
    transition: border-color 0.2s;
  }
  .amazon-ingredient-card.excluded { opacity: 0.45; border-style: dashed; }
  .amazon-ingredient-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 6px;
  }
  .amazon-ingredient-name {
    font-family: 'Playfair Display', serif;
    font-size: 15px;
    color: var(--ink);
  }
  .amazon-ingredient-role {
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--gold);
  }
  .amazon-ingredient-desc { font-size: 12px; color: var(--muted); line-height: 1.6; margin-bottom: 10px; }
  .amazon-ingredient-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .amazon-tag-btn {
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 5px 12px;
    border-radius: 20px;
    border: 1px solid var(--border);
    background: none;
    color: var(--muted);
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: all 0.2s;
  }
  .amazon-tag-btn:hover { border-color: var(--gold); color: var(--gold); }
  .amazon-tag-btn.liked { background: rgba(184,147,90,0.1); border-color: var(--gold); color: var(--gold); }
  .amazon-tag-btn.disliked { background: rgba(26,23,20,0.05); border-color: var(--ink); color: var(--ink); }
  .amazon-tag-btn.excluded { background: rgba(26,23,20,0.07); border-color: var(--ink); color: var(--ink-soft); }
  .amazon-continue-note {
    font-size: 12px;
    color: var(--muted);
    line-height: 1.6;
    margin-bottom: 24px;
    padding: 14px 16px;
    background: rgba(184,147,90,0.06);
    border-left: 2px solid var(--gold);
    border-radius: 0 4px 4px 0;
  }
  .vit-d3-row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 16px 18px;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    background: white;
    margin-bottom: 24px;
  }
  .vit-d3-toggle {
    margin-top: 2px;
    width: 36px;
    height: 20px;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
    transition: background 0.2s;
  }
  .vit-d3-toggle.on { background: var(--gold); }
  .vit-d3-toggle.off { background: var(--faint); }
  .vit-d3-toggle::after {
    content: '';
    position: absolute;
    top: 3px;
    width: 14px; height: 14px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
  }
  .vit-d3-toggle.on::after { left: 19px; }
  .vit-d3-toggle.off::after { left: 3px; }
  .vit-d3-text { flex: 1; }
  .vit-d3-label { font-size: 13px; font-weight: 400; color: var(--ink); margin-bottom: 4px; }
  .vit-d3-desc { font-size: 11px; color: var(--muted); line-height: 1.6; }
`;

const GOAL_LABELS = { SLEEP: "Sleep", STRESS: "Calm", FOCUS: "Focus", BALANCE: "Balance" };

const QUESTIONS = [
  {
    id: "goal", step: "01", total: "09",
    title: "What would you like your blend to address?",
    sub: "This shapes everything — ingredients, scent, and strength.",
    type: "single", cols: "two",
    options: [
      { v: "SLEEP", l: "Bring Me Sleep", s: "Evening wind-down & rest" },
      { v: "STRESS", l: "Bring Me Calm", s: "Quieting the day's noise" },
      { v: "FOCUS", l: "Bring Me Focus", s: "Clarity & mental energy" },
      { v: "BALANCE", l: "Bring Me Balance", s: "Hormonal comfort & steadiness" },
    ]
  },
  {
    id: "detail", step: "02", total: "09",
    title: "A little more detail",
    sub: "This helps us choose the right ingredients for your specific situation.",
    type: "single", cols: "one",
    byGoal: {
      SLEEP: [
        { v: "racing_mind", l: "I can't switch off — racing thoughts at night" },
        { v: "wake_night", l: "I fall asleep but wake during the night" },
        { v: "tension", l: "Physical tension stops me from sleeping deeply" },
        { v: "hormonal", l: "Hormonal changes are disrupting my sleep" },
      ],
      STRESS: [
        { v: "anxiety", l: "Anxiety and persistent worrying thoughts" },
        { v: "overwhelm", l: "Too much on — overwhelm and pressure" },
        { v: "tension", l: "Stress held in my body — tension and tightness" },
        { v: "low_mood", l: "Low mood and emotional flatness" },
      ],
      FOCUS: [
        { v: "fog", l: "Mental fog and fatigue" },
        { v: "motivation", l: "Lack of motivation to begin things" },
        { v: "scattered", l: "Scattered mind — can't settle on one thing" },
        { v: "performance", l: "Pre-performance or pre-exam nerves" },
      ],
      BALANCE: [
        { v: "pms", l: "PMS — monthly emotional and physical shifts" },
        { v: "perimenopause", l: "Perimenopause — navigating a transition" },
        { v: "emotional", l: "Emotional ups and downs, feeling reactive" },
        { v: "grounding", l: "I just need to feel more grounded and steady" },
      ],
    }
  },
  {
    id: "age", step: "03", total: "09",
    title: "Your age range",
    sub: "Helps us weight ingredients appropriately for where you are in life.",
    type: "age",
  },
  {
    id: "gender", step: "04", total: "09",
    title: "How do you identify?",
    sub: "Some ingredients work differently depending on hormonal profile. We ask only to personalise better.",
    type: "single", cols: "one",
    options: [
      { v: "female", l: "Female" },
      { v: "male", l: "Male" },
      { v: "nonbinary", l: "Non-binary" },
      { v: "prefer_not", l: "Prefer not to say" },
    ]
  },
  {
    id: "scent", step: "05", total: "09",
    title: "Scent preferences",
    sub: "Choose up to three. A blend you find genuinely pleasing is one you'll use consistently — and that matters.",
    type: "multi", max: 3, cols: "two",
    options: [
      { v: "floral", l: "Floral", s: "Lavender, rose, chamomile" },
      { v: "citrus", l: "Citrus", s: "Bergamot, lemon — bright & fresh" },
      { v: "woody", l: "Woody & Earthy", s: "Vetiver, cedarwood, sandalwood" },
      { v: "herbal", l: "Fresh & Herbal", s: "Rosemary, marjoram, green" },
      { v: "resinous", l: "Warm & Resinous", s: "Frankincense — deep, meditative" },
      { v: "unsure", l: "Surprise me", s: "Trust the algorithm" },
    ]
  },
  {
    id: "dislikes", step: "06", total: "09",
    title: "Anything to avoid?",
    sub: "We'll make sure these stay out of your blend.",
    type: "multi", cols: "one",
    options: [
      { v: "no_lavender", l: "Lavender — too familiar or soapy" },
      { v: "no_mint", l: "Strong mint or medicinal smells" },
      { v: "no_heavy", l: "Heavy, musky or very earthy scents" },
      { v: "no_sweet", l: "Very sweet or heady florals" },
      { v: "no_citrus", l: "Citrus — too sharp or fleeting" },
      { v: "none", l: "No strong dislikes — open to everything" },
    ]
  },
  {
    id: "sensitivities", step: "07", total: "09",
    title: "Any sensitivities?",
    sub: "Select all that apply.",
    type: "multi", cols: "two",
    options: [
      { v: "nut_allergy", l: "Nut allergy", s: "Sweet almond removed" },
      { v: "vegan", l: "Vegan", s: "All our blends are plant-based" },
      { v: "pregnant", l: "Pregnant or breastfeeding", s: "We'll flag cautions" },
      { v: "none", l: "None", s: "No restrictions" },
    ]
  },
  {
    id: "experience", step: "08", total: "09",
    title: "Have you used essential oils before?",
    sub: "Helps us pitch our explanations at the right level.",
    type: "single", cols: "one",
    options: [
      { v: "never", l: "Never — this is new territory" },
      { v: "occasionally", l: "A little — I've tried a few things" },
      { v: "regularly", l: "Yes — I use them regularly" },
      { v: "professional", l: "I'm a wellness professional", s: "Aromatherapist, herbalist, pharmacist or similar" },
    ]
  },
  {
    id: "naming", step: "09", total: "09",
    title: "Personalise your label",
    sub: "Optional — but a blend with a name on the label is a different object entirely.",
    type: "naming",
  },
];

// Standard ingredients in each Amazon product — shown to returning customers
const STANDARD_BLENDS = {
  SLEEP: [
    { name: "Lavender", role: "Primary · Middle Note", desc: "Many people find lavender supports relaxation and evening wind-down. It is one of the most studied essential oils — traditionally associated with calm and sleep preparation.", ml: 0.6 },
    { name: "Roman Chamomile", role: "Secondary · Middle Note", desc: "Traditionally used for its gentle, soothing character. Softer than lavender, with a warm apple-like scent many find eases mental tension before sleep.", ml: 0.4 },
    { name: "Vetiver", role: "Tertiary · Base Note", desc: "Known as the 'oil of tranquility' in traditional use. Deeply grounding, earthy scent that anchors the blend and gives it lasting depth.", ml: 0.3 },
    { name: "Sandalwood", role: "Accent · Base Note", desc: "Warm, creamy, and deeply calming. Traditionally associated with meditation and mental stillness. Elevates the blend and improves longevity on the skin.", ml: 0.2 },
  ],
  STRESS: [
    { name: "Bergamot", role: "Primary · Top Note", desc: "Bright, citrus-floral — one of the most researched oils for mood. Traditionally associated with lifting emotional heaviness while remaining grounding.", ml: 0.6 },
    { name: "Lavender", role: "Secondary · Middle Note", desc: "Balances Bergamot's brightness with calm. Together they form one of the most classic and well-regarded pairings in aromatherapy for stress support.", ml: 0.4 },
    { name: "Frankincense", role: "Tertiary · Base Note", desc: "Ancient, resinous, meditative. Traditionally used to slow the breath and quiet mental noise. Adds depth and a sense of perspective to the blend.", ml: 0.3 },
    { name: "Ylang Ylang", role: "Accent · Middle Note", desc: "Used in small amounts only — powerful floral with traditional associations with releasing tension. We keep it as a trace accent to avoid it overwhelming the blend.", ml: 0.2 },
  ],
  FOCUS: [
    { name: "Rosemary", role: "Primary · Top/Middle Note", desc: "The most researched essential oil for cognitive function. Traditionally associated with memory and alertness. Sharp, herbaceous, and energising.", ml: 0.6 },
    { name: "Peppermint", role: "Secondary · Top Note", desc: "Cooling and immediately stimulating. Many people find it increases alertness. Used as a secondary note to sharpen Rosemary's effect without dominating.", ml: 0.4 },
    { name: "Lemon", role: "Tertiary · Top Note", desc: "Fresh, bright, and mood-lifting. Traditionally associated with mental clarity. Lightens the blend and makes it feel clean and purposeful.", ml: 0.3 },
    { name: "Cedarwood", role: "Accent · Base Note", desc: "A grounding anchor for what is otherwise a light, fresh blend. Helps sustained focus rather than scattered stimulation — the calm within the energy.", ml: 0.2 },
  ],
  BALANCE: [
    { name: "Clary Sage", role: "Primary · Middle Note", desc: "Traditionally associated with hormonal comfort, particularly around monthly cycles and perimenopause. Warm, slightly nutty scent with a deeply settling quality.", ml: 0.6 },
    { name: "Rose Geranium", role: "Secondary · Middle Note", desc: "Floral and uplifting. Traditionally used for emotional steadiness and hormone-related mood shifts. One of the most versatile and well-regarded oils for feminine wellness.", ml: 0.4 },
    { name: "Roman Chamomile", role: "Tertiary · Middle Note", desc: "Gentle and soothing. Softens the blend and adds emotional warmth. Traditionally associated with releasing tension and reactivity.", ml: 0.3 },
    { name: "Bergamot", role: "Accent · Top Note", desc: "Adds brightness and lift to what is otherwise a warm, grounding blend. Prevents heaviness and gives the blend a fresh, optimistic opening note.", ml: 0.2 },
  ],
};

const AMAZON_QUESTIONS = [
  {
    id: "amazon_product", step: "01", total: "04",
    title: "Which blend did you try?",
    sub: "We'll show you exactly what's in it, then help you personalise.",
    type: "single", cols: "two",
    options: [
      { v: "SLEEP", l: "Bring Me Sleep", s: "Evening wind-down & rest" },
      { v: "STRESS", l: "Bring Me Calm", s: "Quieting the day's noise" },
      { v: "FOCUS", l: "Bring Me Focus", s: "Clarity & mental energy" },
      { v: "BALANCE", l: "Bring Me Balance", s: "Hormonal comfort & steadiness" },
    ]
  },
  {
    id: "amazon_review", step: "02", total: "04",
    title: "Tell us how it worked for you",
    sub: "Rate each ingredient — this helps us refine your personal blend.",
    type: "amazon_review",
  },
  {
    id: "sensitivities", step: "03", total: "04",
    title: "Any sensitivities?",
    sub: "Select all that apply.",
    type: "multi", cols: "two",
    options: [
      { v: "nut_allergy", l: "Nut allergy", s: "Sweet almond removed" },
      { v: "vegan", l: "Vegan", s: "All our blends are plant-based" },
      { v: "pregnant", l: "Pregnant or breastfeeding", s: "We'll flag cautions" },
      { v: "none", l: "None", s: "No restrictions" },
    ]
  },
  {
    id: "naming", step: "04", total: "04",
    title: "Make it yours",
    sub: "Optional — but a blend with your name on it is a different thing entirely.",
    type: "naming",
  },
];

// Amazon ingredient review step
function AmazonReviewStep({ productKey, answers, onAnswer, onNext, onBack }) {
  const ingredients = STANDARD_BLENDS[productKey] || [];
  const [feedback, setFeedback] = useState(answers.amazon_ingredient_feedback || {});
  const [vitD3, setVitD3] = useState(answers.vitd3 !== false);

  const setIngredientFeedback = (name, value) => {
    const updated = { ...feedback, [name]: feedback[name] === value ? null : value };
    setFeedback(updated);
    onAnswer("amazon_ingredient_feedback", updated);
  };

  const handleContinue = () => {
    onAnswer("amazon_ingredient_feedback", feedback);
    onAnswer("vitd3", vitD3);
    onNext();
  };

  return (
    <div className="q-wrap">
      <div className="q-eyebrow">Step 02 of 04</div>
      <h2 className="q-title">Your {productKey.charAt(0) + productKey.slice(1).toLowerCase()} blend — ingredient by ingredient</h2>
      <p className="q-sub">Tell us what worked, what didn't, or what you'd like to swap. You can also exclude ingredients entirely.</p>

      <div className="amazon-continue-note">
        Your personalised blend will keep the ingredients you liked, swap or remove what didn't work, and suggest refinements based on your feedback.
      </div>

      <div className="amazon-ingredients">
        {ingredients.map(ing => {
          const fb = feedback[ing.name];
          return (
            <div key={ing.name} className={`amazon-ingredient-card${fb === "excluded" ? " excluded" : ""}`}>
              <div className="amazon-ingredient-header">
                <span className="amazon-ingredient-name">{ing.name}</span>
                <span className="amazon-ingredient-role">{ing.role}</span>
              </div>
              <p className="amazon-ingredient-desc">{ing.desc}</p>
              <div className="amazon-ingredient-actions">
                <button
                  className={`amazon-tag-btn${fb === "liked" ? " liked" : ""}`}
                  onClick={() => setIngredientFeedback(ing.name, "liked")}
                >✓ Worked well</button>
                <button
                  className={`amazon-tag-btn${fb === "disliked" ? " disliked" : ""}`}
                  onClick={() => setIngredientFeedback(ing.name, "disliked")}
                >↻ Suggest alternative</button>
                <button
                  className={`amazon-tag-btn${fb === "excluded" ? " excluded" : ""}`}
                  onClick={() => setIngredientFeedback(ing.name, "excluded")}
                >✕ Remove entirely</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="vit-d3-row">
        <button
          className={`vit-d3-toggle ${vitD3 ? "on" : "off"}`}
          onClick={() => { setVitD3(v => !v); onAnswer("vitd3", !vitD3); }}
          aria-label="Toggle Vitamin D3"
        />
        <div className="vit-d3-text">
          <div className="vit-d3-label">Include Vitamin D3</div>
          <div className="vit-d3-desc">We add a trace of Vitamin D3 to every blend — because most of us in the UK don't get enough sunlight. Oil-soluble, invisible, no change to scent. You can remove it if you prefer.</div>
        </div>
      </div>

      <div className="nav">
        <button className="btn-back" onClick={onBack}>← Back</button>
        <button className="btn-next" onClick={handleContinue}>Continue →</button>
      </div>
    </div>
  );
}

function EditableName({ value, onChange, maxLen, placeholder, large }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <input
        className="name-edit-input"
        value={value}
        maxLength={maxLen}
        onChange={e => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        autoFocus
        placeholder={placeholder}
      />
    );
  }
  return (
    <span
      className="name-display"
      onClick={() => setEditing(true)}
      title="Click to edit"
      style={large ? {} : { fontSize: "clamp(18px,3vw,24px)", fontStyle: "italic" }}
    >
      {value || <span style={{ opacity: 0.3, fontStyle: "italic" }}>{placeholder}</span>}
    </span>
  );
}

function QuizStep({ q, answers, onAnswer, onNext, onBack, isFirst, isDIY, chatMode }) {
  const [sel, setSel] = useState(answers[q.id] ?? (q.type === "multi" ? [] : null));
  const [freeText, setFreeText] = useState(answers[`${q.id}_free`] || "");

  useEffect(() => {
    setSel(answers[q.id] ?? (q.type === "multi" ? [] : null));
    setFreeText(answers[`${q.id}_free`] || "");
  }, [q.id]);

  const opts = q.byGoal ? (q.byGoal[answers.goal] || []) : (q.options || []);

  const toggle = (v) => {
    if (q.type === "single") { setSel(v); return; }
    if (v === "none") { setSel(["none"]); return; }
    const cur = (sel || []).filter(x => x !== "none");
    if (cur.includes(v)) { setSel(cur.filter(x => x !== v)); return; }
    if (q.max && cur.length >= q.max) return;
    setSel([...cur, v]);
  };

  const hasEarlyFreeText = !!(answers.goal_free || answers.detail_free);

  const canGo = () => {
    if (q.type === "naming") return true;
    if (q.type === "age") return !!answers.age || hasEarlyFreeText;
    if (q.type === "multi") return (sel && sel.length > 0) || freeText.trim() || hasEarlyFreeText;
    return sel !== null || freeText.trim() || hasEarlyFreeText;
  };

  const go = () => {
    if (q.type !== "naming") {
      onAnswer(q.id, sel);
      if (freeText.trim()) onAnswer(`${q.id}_free`, freeText);
    }
    onNext();
  };

  if (q.type === "age") {
    const ages = [
      { v: "under25", l: "Under 25" },
      { v: "25-40", l: "25–40" },
      { v: "41-55", l: "41–55" },
      { v: "55plus", l: "55+" },
    ];
    return (
      <div className="q-wrap" key={q.id}>
        <div className="q-eyebrow">Step {q.step} of {q.total}</div>
        <h2 className="q-title">{q.title}</h2>
        <p className="q-sub">{q.sub}</p>
        <div className="range-grid">
          {ages.map(a => (
            <button key={a.v} className={`range-btn${answers.age === a.v ? " sel" : ""}`}
              onClick={() => onAnswer("age", a.v)}>
              {a.l}
            </button>
          ))}
        </div>
        <div className="nav">
          {!isFirst ? <button className="btn-back" onClick={onBack}>← Back</button> : <span />}
          <button className="btn-next" disabled={!answers.age} onClick={go}>Continue →</button>
        </div>
      </div>
    );
  }

  if (q.type === "naming") {
    return (
      <div className="q-wrap" key={q.id}>
        <div className="q-eyebrow">Step {q.step} of {q.total}</div>
        <h2 className="q-title">{q.title}</h2>
        <p className="q-sub">{q.sub}</p>
        <div className="field-wrap">
          <label className="field-label">
            Your name <span className="field-note">max 20 characters</span>
          </label>
          <input className="txt-input" maxLength={20} placeholder="e.g. Emma"
            value={answers.recipient_name || ""}
            onChange={e => onAnswer("recipient_name", e.target.value)} />
          <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 7, lineHeight: 1.6, opacity: 0.7 }}>
            This appears on your label exactly as written — first name, nickname, or whatever feels right.
          </p>
        </div>
        <div className="field-wrap">
          <label className="field-label">
            Is this a gift? Gifter's name <span className="field-note">max 20 characters</span>
          </label>
          <input className="txt-input" maxLength={20} placeholder="e.g. Mike — leave blank if not a gift"
            value={answers.gifter_name || ""}
            onChange={e => onAnswer("gifter_name", e.target.value)} />
        </div>

        <div className="vit-d3-row">
          <button
            className={`vit-d3-toggle ${answers.vitd3 !== false ? "on" : "off"}`}
            onClick={() => onAnswer("vitd3", answers.vitd3 === false ? true : false)}
            aria-label="Toggle Vitamin D3"
          />
          <div className="vit-d3-text">
            <div className="vit-d3-label">Include Vitamin D3 <span style={{color:"var(--gold)",fontSize:10,letterSpacing:"0.1em"}}>RECOMMENDED</span></div>
            <div className="vit-d3-desc">We add a trace of Vitamin D3 to every blend — because most of us in the UK don't get enough sunlight. Oil-soluble, invisible, no change to scent or feel. You can remove it if you prefer.</div>
          </div>
        </div>

        <div className="nav">
          <button className="btn-back" onClick={onBack}>← Back</button>
          <button className={`btn-next gold`} onClick={go}>Create My Blend →</button>
        </div>
      </div>
    );
  }

  return (
    <div className="q-wrap" key={q.id}>
      <div className="q-eyebrow">Step {q.step} of {q.total}</div>
      <h2 className="q-title">{q.title}</h2>
      <p className="q-sub">{q.sub}</p>

      <div className={`opts ${q.cols}`}>
        {opts.map(o => {
          const isSel = q.type === "multi" ? (sel || []).includes(o.v) : sel === o.v;
          return (
            <button key={o.v} className={`opt${isSel ? " sel" : ""}`} onClick={() => toggle(o.v)}>
              <div className="opt-check" />
              <span className="opt-label">{o.l}</span>
              {o.s && <span className="opt-sub">{o.s}</span>}
            </button>
          );
        })}
      </div>

      {/* 'Other' context box — detail step only */}
      {q.id === "detail" && (
        <div className="freetext-wrap">
          <label className="field-label" style={{marginBottom: 6}}>Anything else we should know?</label>
          <textarea
            className="txt-area"
            rows={3}
            placeholder="e.g. I'm going through bereavement, or dealing with a specific health condition — any context helps us get the blend right."
            value={freeText}
            onChange={e => { setFreeText(e.target.value); onAnswer(`${q.id}_free`, e.target.value); }}
          />
        </div>
      )}

      {chatMode === "freetext" && q.id === "goal" && (
        <div className="freetext-wrap">
          <label className="field-label" style={{marginBottom: 6}}>Or describe the condition you'd like to address</label>
          <textarea
            className="txt-area"
            rows={3}
            placeholder="e.g. Female, 55, severe Parkinson's, chairbound — likes aromatherapy. Or: 18-year-old revising for A-levels, needs focus and energy."
            value={freeText}
            onChange={e => { setFreeText(e.target.value); onAnswer(`${q.id}_free`, e.target.value); }}
          />
        </div>
      )}

      <div className="nav">
        {!isFirst ? <button className="btn-back" onClick={onBack}>← Back</button> : <span />}
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          {(q.type === "multi" || hasEarlyFreeText) && <button className="btn-skip" onClick={go}>Skip</button>}
          <button className="btn-next" disabled={!canGo()} onClick={go}>Continue →</button>
        </div>
      </div>
    </div>
  );
}

// Simple SVG bottle illustration
function BottleSVG({ goalLabel = "Sleep", recipientName = "" }) {
  const displayName = recipientName ? `Made for ${recipientName}` : "My Blend";
  return (
    <svg width="70" height="180" viewBox="0 0 70 180" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Cap */}
      <rect x="22" y="4" width="26" height="16" rx="4" fill="#b8935a"/>
      <rect x="25" y="20" width="20" height="8" rx="2" fill="#c9a870"/>
      {/* Neck */}
      <rect x="27" y="28" width="16" height="20" rx="2" fill="#e8e0d0"/>
      <rect x="28" y="28" width="2" height="20" fill="rgba(255,255,255,0.4)"/>
      {/* Shoulder curve */}
      <path d="M14 50 Q14 42 27 42 L43 42 Q56 42 56 50 L56 56 L14 56 Z" fill="#f0e8dc"/>
      {/* Body */}
      <rect x="14" y="56" width="42" height="106" rx="4" fill="#f0e8dc"/>
      <rect x="16" y="56" width="5" height="106" fill="rgba(255,255,255,0.35)"/>
      {/* Label area */}
      <rect x="17" y="68" width="36" height="76" rx="2" fill="white" opacity="0.9"/>
      <rect x="17" y="68" width="36" height="2.5" fill="#b8935a"/>
      {/* Label text lines (decorative) */}
      <text x="35" y="84" textAnchor="middle" fontFamily="serif" fontSize="4" fill="#b8935a" letterSpacing="1">MY ALCHEMY</text>
      <text x="35" y="95" textAnchor="middle" fontFamily="serif" fontSize="6.5" fontWeight="500" fill="#1a1714">Bring Me</text>
      <text x="35" y="104" textAnchor="middle" fontFamily="serif" fontSize="7" fontWeight="600" fill="#1a1714">{goalLabel}</text>
      <line x1="22" y1="108" x2="48" y2="108" stroke="#e0d4c4" strokeWidth="0.5"/>
      <text x="35" y="116" textAnchor="middle" fontFamily="serif" fontSize="4.5" fontStyle="italic" fill="#6b6057">{displayName.substring(0, 16)}</text>
      {/* QR placeholder */}
      <rect x="26" y="122" width="18" height="18" rx="1" fill="#f5f0e8"/>
      <rect x="27" y="123" width="7" height="7" rx="0.5" fill="#c4b89a"/>
      <rect x="35" y="123" width="7" height="7" rx="0.5" fill="#c4b89a"/>
      <rect x="27" y="131" width="7" height="7" rx="0.5" fill="#c4b89a"/>
      <rect x="36" y="131" width="5" height="3" rx="0.5" fill="#c4b89a"/>
      <rect x="36" y="135" width="5" height="3" rx="0.5" fill="#c4b89a"/>
      {/* Bottom */}
      <rect x="14" y="158" width="42" height="6" rx="2" fill="#e0d4c4"/>
      <ellipse cx="35" cy="164" rx="21" ry="4" fill="#d4c8b8"/>
    </svg>
  );
}

// The live label preview — exact proportions for 50x30mm PRIMERA label
function LabelPreview({ goalLabel, recipientName, gifterName, scentTags, ingredients }) {
  const inci = (ingredients || [])
    .map(i => i.name.split("(")[0].trim())
    .join(", ");

  return (
    <div>
      <div className="label-preview-title">Bottle label preview · 50 × 30mm</div>
      <div className="label-preview">
        <div className="label-top">
          <div className="label-brand">My Alchemy</div>
          <div className="label-goal">Bring Me {goalLabel}</div>
          {recipientName && <div className="label-name">Made for {recipientName}</div>}
          {gifterName && <div className="label-gifted">from {gifterName}</div>}
          {scentTags && scentTags.length > 0 && (
            <div className="label-tags">
              {scentTags.map(t => <span key={t} className="label-tag">{t}</span>)}
            </div>
          )}
        </div>
        <div className="label-bottom">
          <div className="label-legal">
            10ml · External use only · Patch test first<br />
            {inci && `Contains: ${inci.substring(0, 60)}${inci.length > 60 ? "..." : ""}`}
          </div>
          <div className="label-qr-placeholder">QR<br/>code</div>
        </div>
      </div>
    </div>
  );
}

function LabelSection({ result, answers, goalLabel, recipientName, gifterName }) {
  const [showGiftTag, setShowGiftTag] = useState(!!answers.gifter_name);
  const maxMsg = 60;
  const defaultMsg = answers.gifter_name
    ? `With love, ${answers.gifter_name}`
    : `A blend created just for you.`;
  const [giftMessage, setGiftMessage] = useState(defaultMsg);

  const printLabel = () => {
    const win = window.open("", "_blank", "width=600,height=400");
    win.document.write(`
      <html><head><title>Bottle Label — ${recipientName || "My Alchemy"}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=DM+Sans:wght@300;400&display=swap');
        @page { size: 50mm 30mm; margin: 0; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { width: 50mm; height: 30mm; font-family: 'DM Sans', sans-serif; background: white; padding: 2mm 2.5mm; display: flex; flex-direction: column; justify-content: space-between; }
        .top-bar { height: 0.8mm; background: linear-gradient(90deg, #b8935a, #d4ad7a); margin: -2mm -2.5mm 1.5mm; }
        .brand { font-family: 'Playfair Display', serif; font-size: 4pt; letter-spacing: 0.15em; text-transform: uppercase; color: #b8935a; }
        .goal { font-family: 'Playfair Display', serif; font-size: 8pt; font-weight: 500; color: #1a1714; line-height: 1.1; }
        .pname { font-family: 'Playfair Display', serif; font-size: 6pt; font-style: italic; color: #6b6057; }
        .gifted { font-size: 4pt; color: #9a8878; }
        .tags { display: flex; gap: 1mm; flex-wrap: wrap; margin-top: 0.5mm; }
        .tag { font-size: 3.5pt; letter-spacing: 0.1em; text-transform: uppercase; color: #b8935a; border: 0.3pt solid #b8935a; padding: 0.3mm 1mm; border-radius: 0.5mm; }
        .bottom { display: flex; justify-content: space-between; align-items: flex-end; }
        .legal { font-size: 3pt; color: #aaa; line-height: 1.4; max-width: 38mm; }
        .qr { width: 8mm; height: 8mm; background: #f5f5f5; border: 0.3pt solid #ddd; display: flex; align-items: center; justify-content: center; font-size: 3pt; color: #bbb; text-align: center; border-radius: 0.5mm; }
      </style></head>
      <body>
        <div class="top-bar"></div>
        <div>
          <div class="brand">My Alchemy</div>
          <div class="goal">Bring Me ${goalLabel}</div>
          ${recipientName ? `<div class="pname">Made for ${recipientName}</div>` : ""}
          ${gifterName ? `<div class="gifted">from ${gifterName}</div>` : ""}
          ${result.scent_tags ? `<div class="tags">${result.scent_tags.map(t => `<span class="tag">${t}</span>`).join("")}</div>` : ""}
        </div>
        <div class="bottom">
          <div class="legal">10ml · External use only · Patch test before first use<br/>${(result.ingredients || []).map(i => i.name.split("(")[0].trim()).join(", ")}, Tocopherol (Vit E), Simmondsia Chinensis (Jojoba) Seed Oil</div>
          <div class="qr">QR<br/>code</div>
        </div>
        <script>window.onload = () => window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  const printGiftTag = () => {
    const win = window.open("", "_blank", "width=600,height=400");
    win.document.write(`
      <html><head><title>Gift Tag</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&display=swap');
        @page { size: 85mm 55mm; margin: 0; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { width: 85mm; height: 55mm; background: #faf7f2; padding: 5mm 6mm; display: flex; flex-direction: column; justify-content: space-between; font-family: 'Playfair Display', serif; }
        .top-bar { height: 1mm; background: linear-gradient(90deg, transparent, #b8935a, transparent); margin: -5mm -6mm 3mm; }
        .brand { font-size: 5pt; letter-spacing: 0.3em; text-transform: uppercase; color: #b8935a; margin-bottom: 3mm; font-family: 'Playfair Display', serif; }
        .message { font-size: 9pt; font-style: italic; color: #1a1714; line-height: 1.55; flex: 1; }
        .footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 3mm; }
        .from { font-size: 6pt; color: #9a8878; letter-spacing: 0.1em; }
        .blend { font-size: 5pt; letter-spacing: 0.15em; text-transform: uppercase; color: #b8935a; }
      </style></head>
      <body>
        <div class="top-bar"></div>
        <div class="brand">My Alchemy</div>
        <div class="message">${giftMessage}</div>
        <div class="footer">
          <div class="from">From ${gifterName || answers.gifter_name || ""}</div>
          <div class="blend">Bring Me ${goalLabel}</div>
        </div>
        <script>window.onload = () => window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  return (
    <div className="label-section">
      <div className="label-section-header">
        <div className="section-label" style={{ margin: 0 }}>Your Label & Print Files</div>
      </div>

      <div className="bottle-mockup-wrap">
        <div className="bottle-svg-wrap">
          <BottleSVG goalLabel={goalLabel} recipientName={recipientName} />
        </div>
        <LabelPreview
          goalLabel={goalLabel}
          recipientName={recipientName}
          gifterName={gifterName}
          scentTags={result.scent_tags}
          ingredients={result.ingredients}
        />
      </div>

      <div className="print-btns">
        <button className="btn-print primary-print" onClick={printLabel}>
          Print Bottle Label →
        </button>
      </div>
      <p className="primer-note">
        Formatted for PRIMERA LX500 · 50 × 30mm vinyl label · Opens print dialog
      </p>

      {/* Gift tag */}
      <div className="gift-tag-wrap">
        <div className="gift-toggle">
          <button
            className={`gift-toggle-btn${showGiftTag ? " active" : ""}`}
            onClick={() => setShowGiftTag(v => !v)}
          >
            {showGiftTag ? "✓ Gift Tag Added" : "+ Add Gift Tag"}
          </button>
          <span className="gift-toggle-price">£1.00 · Printed card, company logo reverse</span>
        </div>

        {showGiftTag && (
          <>
            <div className="gift-tag-preview">
              <div className="gift-tag-brand">My Alchemy</div>
              <div className="gift-tag-message">{giftMessage || "Your message will appear here..."}</div>
              <div className="gift-tag-footer">
                <div className="gift-tag-from">From {gifterName || answers.gifter_name || "—"}</div>
                <div className="gift-tag-blend">Bring Me {goalLabel}</div>
              </div>
            </div>

            <textarea
              className="gift-message-input"
              rows={3}
              maxLength={maxMsg}
              value={giftMessage}
              onChange={e => setGiftMessage(e.target.value)}
              placeholder="Write your personal message..."
            />
            <div className="gift-message-count">{giftMessage.length} / {maxMsg}</div>

            <div className="print-btns">
              <button className="btn-print primary-print" onClick={printGiftTag}>
                Print Gift Tag →
              </button>
            </div>
            <p className="primer-note">
              Formatted for PRIMERA LX500 · 85 × 55mm · Card stock · Opens print dialog
            </p>
          </>
        )}
      </div>
    </div>
  );
}


// Floating Q&A-only support chat — shown during steps 3-8
function FloatingChat({ answers }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Ask me anything — about the quiz, the ingredients, or what might work for your situation." }
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);

  const send = async () => {
    if (!input.trim() || thinking) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", text: userMsg }]);
    setThinking(true);
    try {
      const res = await fetch(ANTHROPIC_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 300,
          system: KNOWLEDGE + "\n\nYou are a support assistant mid-quiz. Answer wellness or ingredient questions in 2-3 sentences. Warm but measured. No ingredient-adding actions. Plain text only, no JSON. Current answers: Goal=" + (answers.goal||"not yet") + ", Detail=" + (answers.detail||"not yet") + ", Age=" + (answers.age||"not yet") + ".",
          messages: [{ role: "user", content: userMsg }],
        }),
      });
      const data = await res.json();
      const text = (data.content || []).map(b => b.text || "").join("").trim();
      setMessages(m => [...m, { role: "assistant", text }]);
    } catch(e) {
      setMessages(m => [...m, { role: "assistant", text: "Sorry, something went wrong." }]);
    }
    setThinking(false);
  };

  return (
    <div className="floating-chat">
      <button className={"floating-chat-toggle" + (open ? " open" : "")} onClick={() => setOpen(o => !o)}>
        <span className="floating-chat-toggle-icon">{open ? "✕" : "?"}</span>
        {open ? "Close" : "Questions? Ask us"}
      </button>
      {open && (
        <div className="floating-chat-panel">
          <div className="floating-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={"chat-msg " + m.role}>{m.text}</div>
            ))}
            {thinking && (
              <div className="chat-msg assistant thinking">
                <div className="loader-dot" /><div className="loader-dot" /><div className="loader-dot" />
              </div>
            )}
          </div>
          <div className="floating-chat-input-row">
            <input className="floating-chat-input" placeholder="Ask a question..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} />
            <button className="floating-chat-send" onClick={send} disabled={thinking || !input.trim()}>→</button>
          </div>
        </div>
      )}
    </div>
  );
}
function InlineChat({ answers, result, extraDoses, setExtraDoses, basePrice, setLivePrice }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "I can answer questions about any of the ingredients in your blend, explain what the evidence says, or add something if you'd like to explore further." }
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const messagesEndRef = useState(null);

  const ingredientList = Object.keys(INGREDIENTS).join(", ");
  const currentBlendIngredients = result ? (result.ingredients || []).map(i => i.name.split("(")[0].trim()) : [];
  const extraDosesSummary = Object.entries(extraDoses).filter(([,d]) => d > 0).map(([k,v]) => `${k}: ${DOSE_LABELS[v-1] || v} dose`).join(", ") || "none";

  const calcTotal = (doses) => {
    return Object.entries(doses).reduce((sum, [ing, d]) => sum + getPrice(ing) * d, 0);
  };

  const send = async () => {
    if (!input.trim() || thinking) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", text: userMsg }]);
    setThinking(true);

    const ingredientDetails = Object.entries(INGREDIENTS)
      .map(([name, i]) => `${name}: scent="${i.scent}", maxDoses=${i.maxDoses}, price=£${i.price}/dose, premium=${i.premium}`)
      .join("\n");

    const systemPrompt = `${KNOWLEDGE}

You are the MY ALCHEMY inline chat advisor, visible throughout the quiz and on the result screen.

Current customer context:
- Goal: ${answers.goal || "not yet selected"}
- Current blend ingredients: ${currentBlendIngredients.join(", ") || "not yet generated"}
- Extra additions so far: ${extraDosesSummary}
- Base blend price: £${basePrice || "32"}

Full ingredient library with details:
${ingredientDetails}

RESPONSE RULES — respond in 2-3 sentences max, then end with ONE JSON block:

1. If customer asks about or requests a KNOWN ingredient:
   - Tell them what it does honestly, mention the scent profile
   - If maxDoses=1: it's a single addition
   - If maxDoses>1: mention Standard/Enriched/Deeply Enriched options exist
   - End with: {"action":"suggest_ingredient","ingredient":"EXACT NAME","maxDoses":NUMBER,"pricePerDose":NUMBER,"isPremium":BOOLEAN}

2. If customer asks about an UNAVAILABLE ingredient (melatonin, CBD etc):
   - Explain why, suggest closest alternative
   - End with: {"action":"none"}

3. General wellness/scent/ingredient questions:
   - Answer warmly and honestly
   - End with: {"action":"none"}

Always end with a JSON block on its own line. Keep language warm, honest, never overclaim.`;

    try {
      const res = await fetch(ANTHROPIC_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 400,
          system: systemPrompt,
          messages: [{ role: "user", content: userMsg }],
        }),
      });
      const data = await res.json();
      const fullText = (data.content || []).map(b => b.text || "").join("");

      let action = { action: "none" };
      let displayText = fullText;
      try {
        const jsonMatch = fullText.match(/\{[\s\S]*"action"[\s\S]*\}/);
        if (jsonMatch) {
          action = JSON.parse(jsonMatch[0]);
          displayText = fullText.replace(jsonMatch[0], "").trim();
        }
      } catch(e) {}

      setMessages(m => [...m, { role: "assistant", text: displayText, action }]);
    } catch(e) {
      setMessages(m => [...m, { role: "assistant", text: "Sorry, something went wrong. Please try again." }]);
    }
    setThinking(false);
  };

  const handleSetDose = (ingredient, dose, pricePerDose) => {
    const updated = { ...extraDoses, [ingredient]: dose };
    if (dose === 0) delete updated[ingredient];
    setExtraDoses(updated);
    const delta = calcTotal(updated);
    setLivePrice((parseInt(basePrice) || 32) + delta);
  };

  return (
    <div className="inline-chat">
      <div className="inline-chat-header">
        <span className="inline-chat-title">Ingredient Advisor</span>
        <span className="inline-chat-sub">Questions or additions · we're here</span>
      </div>
      <div className="inline-chat-messages">
        {messages.map((m, i) => (
          <div key={i}>
            <div className={`chat-msg ${m.role}`}>{m.text}</div>
            {m.role === "assistant" && m.action && m.action.action === "suggest_ingredient" && (() => {
              const ing = m.action.ingredient;
              const maxD = m.action.maxDoses || 1;
              const pricePerDose = m.action.pricePerDose || 0;
              const currentDose = extraDoses[ing] || 0;
              const isPremium = m.action.isPremium;

              if (maxD === 1) {
                // Single add — "Add to Blend" button
                return (
                  <div className="chat-action-row">
                    {currentDose === 0 ? (
                      <button className="chat-add-single" onClick={() => handleSetDose(ing, 1, pricePerDose)}>
                        {isPremium ? `✦ Add ${ing} to Blend` : `+ Add ${ing} to Blend`}
                        {pricePerDose > 0 && <span className="chat-add-price"> · +£{pricePerDose}</span>}
                      </button>
                    ) : (
                      <div className="chat-added-confirm">
                        ✓ {ing} added to your blend
                        <button className="chat-remove-btn" onClick={() => handleSetDose(ing, 0, pricePerDose)}>Remove</button>
                      </div>
                    )}
                  </div>
                );
              } else {
                // Multi-dose — Standard / Enriched / Deeply Enriched
                const labels = DOSE_LABELS.slice(0, maxD);
                return (
                  <div className="chat-action-row">
                    <div className="chat-dose-label">Choose your level:</div>
                    <div className="chat-dose-options">
                      {labels.map((label, idx) => {
                        const dose = idx + 1;
                        const extraCost = pricePerDose * dose;
                        return (
                          <button
                            key={dose}
                            className={`chat-dose-option${currentDose === dose ? " selected" : ""}`}
                            onClick={() => handleSetDose(ing, currentDose === dose ? 0 : dose, pricePerDose)}
                          >
                            <span className="dose-option-label">{label}</span>
                            {extraCost > 0 && <span className="dose-option-price">+£{extraCost}</span>}
                            {extraCost === 0 && <span className="dose-option-price">Included</span>}
                          </button>
                        );
                      })}
                    </div>
                    {currentDose > 0 && (
                      <div className="chat-added-confirm">✓ {ing} · {DOSE_LABELS[currentDose-1]} added</div>
                    )}
                  </div>
                );
              }
            })()}
          </div>
        ))}
        {thinking && (
          <div className="chat-msg assistant thinking">
            <div className="loader-dot" /><div className="loader-dot" /><div className="loader-dot" />
          </div>
        )}
      </div>
      <div className="inline-chat-input-row">
        <input
          className="inline-chat-input"
          placeholder="Ask about an ingredient, or ask us to add something..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
        />
        <button className="inline-chat-send" onClick={send} disabled={thinking || !input.trim()}>→</button>
      </div>
    </div>
  );
}

function Loading({ name }) {
  const lines = ["Reading your preferences...", "Consulting the ingredient library...", "Balancing your scent profile...", "Crafting your formula..."];
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI(x => Math.min(x + 1, lines.length - 1)), 1200); return () => clearInterval(t); }, []);
  return (
    <div className="loading">
      <div className="loading-eyebrow">Creating your formula</div>
      <div className="loading-title">{name ? `${name}'s blend` : "Your blend"}</div>
      <div className="loading-line">{lines[i]}</div>
      <div className="loader-dots">
        <div className="loader-dot" /><div className="loader-dot" /><div className="loader-dot" />
      </div>
    </div>
  );
}

function Result({ result, answers, isDIY, onRestart, onUpgrade, upgraded, livePrice, inlineChat }) {
  const goalLabel = GOAL_LABELS[answers.goal] || "";
  const [recipientName, setRecipientName] = useState(answers.recipient_name || "");
  const [gifterName, setGifterName] = useState(answers.gifter_name || "");

  return (
    <div className="result">
      <div className="result-hero">
        <div className="result-eyebrow">Your Personal Formula</div>

        {/* Always show "Bring Me X" at top */}
        <span className="bring-me-label">Bring Me {goalLabel}</span>

        {/* Editable personal name below — defaults to "[Name]'s Blend" */}
        <div className="name-edit-wrap">
          <EditableName
            value={recipientName || (answers.recipient_name ? `${answers.recipient_name}'s Blend` : "Your Blend")}
            onChange={setRecipientName}
            maxLen={20}
            placeholder="Your Blend"
            large
          />
          <div className="name-hint">This appears on your label · click to edit · max 20 characters</div>
        </div>

        {gifterName && (
          <div className="gifted-by">
            <EditableName
              value={`Gifted by ${gifterName}`}
              onChange={v => setGifterName(v.replace("Gifted by ", ""))}
              maxLen={30}
              placeholder="Gifted by..."
            />
          </div>
        )}

        {result.scent_tags && (
          <div className="scent-tags">
            {result.scent_tags.map(t => <span key={t} className="stag">{t}</span>)}
          </div>
        )}
      </div>

      {result.summary && (
        <div style={{ marginBottom: 36 }}>
          <div className="section-label">About This Blend</div>
          <p style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.8 }}>{result.summary}</p>
        </div>
      )}

      <div className="ing-list">
        <div className="section-label">Your Ingredients</div>
        {(result.ingredients || []).map((ing, i) => (
          <div key={i} className="ing">
            <div className="ing-dot" />
            <div>
              <div className="ing-name">{ing.name}</div>
              <div className="ing-note">{ing.reason}</div>
              {isDIY && ing.ml && <div className="ing-ml">{ing.ml} · for 10ml bottle</div>}
            </div>
          </div>
        ))}
        {isDIY && (
          <div className="ing">
            <div className="ing-dot" />
            <div>
              <div className="ing-name">Vitamin E (Tocopherol)</div>
              <div className="ing-note">Antioxidant preservation — included in all blends.</div>
              {isDIY && <div className="ing-ml">0.2ml · for 10ml bottle</div>}
            </div>
          </div>
        )}
      </div>

      {result.scent_note && (
        <div className="advisory">
          <strong>Scent Note</strong>
          {result.scent_note}
        </div>
      )}

      {/* Premium upgrade suggestion */}
      {!upgraded && result.upgrade && (
        <div className="upgrade-box">
          <strong>Elevate This Blend</strong>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 400, color: "var(--ink)", marginBottom: 10 }}>
            Add {result.upgrade.ingredient}
          </div>
          <div className="upgrade-desc">{result.upgrade.description}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <span className="upgrade-price">{result.upgrade.price}</span>
            <button className="upgrade-cta" onClick={() => onUpgrade(result.upgrade)}>
              Add to My Blend →
            </button>
          </div>
        </div>
      )}

      {/* Variations */}
      {result.variations && result.variations.length > 0 && (
        <div className="variations">
          <div className="section-label">Want to Compare?</div>
          <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 14, lineHeight: 1.65 }}>
            Because individual responses vary, trying two variations can reveal what works best for your body. Still far simpler — and often cheaper — than sourcing the oils yourself.
          </p>
          {result.variations.map((v, i) => (
            <div key={i} className="var-card">
              <div className="var-header">
                <span className="var-name">{v.name}</span>
                <span className="var-discount">{v.discount} off</span>
              </div>
              <div className="var-desc">{v.description}</div>
            </div>
          ))}
        </div>
      )}

      {isDIY && (
        <div className="diy-notice">
          <strong>DIY Recipe</strong>
          All proportions shown above are for a 10ml roller-ball. You'll need: pipettes or syringes, 10ml roller bottles, and each ingredient from a reputable supplier. Total ingredient cost to make one bottle yourself: approximately £8–15 depending on what you have. Or we make it for you, quality-checked, for {result.price || "£32"}.
        </div>
      )}

      {/* Label preview & print section */}
      <LabelSection
        result={result}
        answers={answers}
        goalLabel={goalLabel}
        recipientName={recipientName || (answers.recipient_name ? `${answers.recipient_name}'s Blend` : "Your Blend")}
        gifterName={gifterName || answers.gifter_name || ""}
      />

      {livePrice && livePrice !== (parseInt((result.price || "32").replace(/[^0-9]/g, "")) || 32) && (
        <div className="price-update-banner">
          <span className="price-update-label">Updated price with your additions</span>
          <span className="price-update-value">£{livePrice}</span>
        </div>
      )}

      <div className="price-row">
        <div className="price-main">{livePrice ? `£${livePrice}` : (result.price || "£32")}</div>
        <div className="price-meta">
          <div className="price-tier">{result.price_tier || "Standard blend · 10ml"}</div>
          {result.subscription_note && (
            <div className="price-sub">{result.subscription_note}</div>
          )}
        </div>
      </div>

      <div className="ctas">
        {isDIY
          ? <>
            <button className="btn-buy gold-btn">We'll Make It For You — {result.price || "£32"}</button>
            <button className="btn-outline" onClick={onRestart}>Adjust Formula</button>
          </>
          : <>
            <button className="btn-buy">Add to Order</button>
            <button className="btn-outline" onClick={onRestart}>Adjust Formula</button>
          </>
        }
      </div>

      {inlineChat}

      <p className="disclaimer">
        This is a cosmetic product for external use only. It is not intended to diagnose, treat, cure, or prevent any disease or medical condition. Information reflects traditional uses and where available, current research. Individual responses vary significantly. Patch test before first use. If pregnant, breastfeeding, or taking medication, consult a healthcare professional before use. © My Alchemy.
      </p>
    </div>
  );
}

// Entry screen — shown before quiz, lets user choose their path
function QRScreen({ onBack }) {
  // A clean decorative QR-style SVG - fully self-contained, no overflow
  return (
    <div className="qr-screen">
      <div className="q-eyebrow" style={{fontSize:10, letterSpacing:"0.25em", textTransform:"uppercase", color:"var(--gold)", marginBottom:16}}>Review your bespoke blend</div>
      <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"clamp(22px,4vw,32px)", fontWeight:400, color:"var(--ink)", marginBottom:16, lineHeight:1.25}}>Scan the QR code on your bottle</h2>
      <p style={{fontSize:14, color:"var(--muted)", lineHeight:1.8, maxWidth:440, margin:"0 auto 28px"}}>
        The QR code on your personalised bottle links directly to your formula — every ingredient, why it was chosen for you, and the option to adjust or reorder.
      </p>

      <div className="qr-mock">
        <svg width="148" height="148" viewBox="0 0 148 148" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Top-left finder */}
          <rect x="8" y="8" width="40" height="40" rx="4" fill="#1a1714"/>
          <rect x="14" y="14" width="28" height="28" rx="2" fill="white"/>
          <rect x="20" y="20" width="16" height="16" rx="1" fill="#1a1714"/>
          {/* Top-right finder */}
          <rect x="100" y="8" width="40" height="40" rx="4" fill="#1a1714"/>
          <rect x="106" y="14" width="28" height="28" rx="2" fill="white"/>
          <rect x="112" y="20" width="16" height="16" rx="1" fill="#1a1714"/>
          {/* Bottom-left finder */}
          <rect x="8" y="100" width="40" height="40" rx="4" fill="#1a1714"/>
          <rect x="14" y="106" width="28" height="28" rx="2" fill="white"/>
          <rect x="20" y="112" width="16" height="16" rx="1" fill="#1a1714"/>
          {/* Data dots - decorative pattern */}
          {[56,62,68,74,80,86,92].map((x,i) => [56,62,68,74,80,86,92].map((y,j) => {
            const on = (i+j)%3!==0 && !((i===3)&&(j===3));
            return on ? <rect key={`${i}-${j}`} x={x} y={y} width="5" height="5" rx="1" fill="#1a1714" opacity={0.7+(i*j%3)*0.1}/> : null;
          }))}
          {/* Timing dots */}
          {[56,68,80,92].map(x => <rect key={`t${x}`} x={x} y={50} width="5" height="5" rx="1" fill="#1a1714" opacity="0.5"/>)}
          {[56,68,80,92].map(y => <rect key={`tv${y}`} x={50} y={y} width="5" height="5" rx="1" fill="#1a1714" opacity="0.5"/>)}
        </svg>
      </div>

      <div style={{maxWidth:420, margin:"0 auto 24px", fontSize:14, color:"var(--muted)", lineHeight:1.8}}>
        <p><strong style={{fontWeight:500, color:"var(--ink)"}}>On your phone:</strong> point your camera at the QR code — no app needed. It opens instantly.</p>
        <p style={{marginTop:10}}><strong style={{fontWeight:500, color:"var(--ink)"}}>On a desktop:</strong> we'll send a link to your phone via text or email so you can scan from there.</p>
      </div>

      <div style={{padding:"16px 20px", background:"rgba(184,147,90,0.06)", border:"1px solid rgba(184,147,90,0.18)", borderRadius:8, maxWidth:420, margin:"0 auto 32px", fontSize:12, color:"var(--muted)", lineHeight:1.7}}>
        <strong style={{fontSize:9, letterSpacing:"0.2em", textTransform:"uppercase", color:"var(--gold)", display:"block", marginBottom:6}}>Demo note</strong>
        In the live product, scanning your bottle's QR code loads your exact formula. No bottles with active QR codes exist yet — but this is precisely how the returning customer journey will work.
      </div>

      <button className="btn-back" style={{fontSize:13}} onClick={onBack}>← Back</button>
    </div>
  );
}

function EntryScreen({ onNew, onAmend, onQR }) {
  const [showAmendOptions, setShowAmendOptions] = useState(false);

  if (showAmendOptions) {
    return (
      <div className="entry-wrap">
        <div className="entry-eyebrow">Refine your blend</div>
        <h2 className="entry-title">How did you come to us?</h2>
        <p className="entry-sub">We'll pick up from where you are.</p>
        <div className="entry-paths" style={{gridTemplateColumns:"1fr"}}>
          <button className="entry-path" onClick={onAmend}>
            <span className="entry-path-icon">⟳</span>
            <span className="entry-path-title">I tried one of your standard blends</span>
            <span className="entry-path-sub">Bought from Amazon, a retailer, or received as a gift. We'll show you what's in it and help you personalise it.</span>
          </button>
          <button className="entry-path" onClick={onQR}>
            <span className="entry-path-icon">▦</span>
            <span className="entry-path-title">I have a QR code on my bottle</span>
            <span className="entry-path-sub">You already have a bespoke blend. Scan the QR code to review your formula, adjust ingredients, or reorder.</span>
          </button>
        </div>
        <button className="btn-back" style={{marginTop:8, fontSize:13}} onClick={() => setShowAmendOptions(false)}>← Back</button>
      </div>
    );
  }

  return (
    <div className="entry-wrap">
      <h2 className="entry-title">Create your own wellness blend, designed around your preferences and needs (£24)</h2>
      <p className="entry-sub">Answer a few questions about what you need and we'll build a personalised formula from the ingredients that suit you best.</p>
      <div className="entry-paths">
        <button className="entry-path" onClick={onNew}>
          <span className="entry-path-icon">✦</span>
          <span className="entry-path-title">Create a new blend</span>
          <span className="entry-path-sub">Start from scratch. We'll guide you through a short set of questions.</span>
        </button>
        <button className="entry-path" onClick={() => setShowAmendOptions(true)}>
          <span className="entry-path-icon">⟳</span>
          <span className="entry-path-title">Amend a previous blend</span>
          <span className="entry-path-sub">Tried one of our blends, or received one as a gift? We'll help you refine it.</span>
        </button>
      </div>
    </div>
  );
}

export default function MyAlchemyApp() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("entry");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isDIY, setIsDIY] = useState(false);
  const [upgraded, setUpgraded] = useState(false);
  const [isAmazon, setIsAmazon] = useState(false);
  const [extraDoses, setExtraDoses] = useState({});
  const [livePrice, setLivePrice] = useState(null);

  const questions = isAmazon ? AMAZON_QUESTIONS : QUESTIONS;
  const totalSteps = questions.length;
  const progress = (step / totalSteps) * 100;

  const handleAnswer = useCallback((k, v) => {
    setAnswers(prev => ({ ...prev, [k]: v }));
  }, []);

  const handleNext = () => {
    if (step < questions.length - 1) setStep(s => s + 1);
    else isAmazon ? generateAmazon() : generate();
  };

  const generateAmazon = async () => {
    setPhase("loading");
    setError(null);

    const productKey = answers.amazon_product;
    const standardIngredients = STANDARD_BLENDS[productKey] || [];
    const feedback = answers.amazon_ingredient_feedback || {};
    const vitD3 = answers.vitd3 !== false;

    const feedbackSummary = standardIngredients.map(ing => {
      const fb = feedback[ing.name];
      return `${ing.name} (${ing.role}): ${fb === "liked" ? "Customer liked this — keep it" : fb === "disliked" ? "Customer wants an alternative — suggest a swap" : fb === "excluded" ? "Customer wants to REMOVE this entirely" : "No specific feedback — keep unless it clashes"}`;
    }).join("\n");

    const prompt = `
This is a RETURNING AMAZON CUSTOMER who bought the standard "${productKey}" blend and wants a personalised version.

Standard blend they tried:
${standardIngredients.map(i => `- ${i.name} (${i.role})`).join("\n")}

Their ingredient-by-ingredient feedback:
${feedbackSummary}

Vitamin D3 included: ${vitD3 ? "YES — customer opted in" : "NO — customer opted out"}
Sensitivities: ${JSON.stringify(answers.sensitivities)}
Recipient name: "${answers.recipient_name || "not given"}"
Gifter name: "${answers.gifter_name || "not a gift"}"
DIY mode: ${isDIY}

IMPORTANT RULES FOR THIS PATH:
- Keep ingredients marked "liked" — do not swap them
- Suggest a genuine alternative for ingredients marked "disliked" — explain why the alternative might work better
- Remove ingredients marked "excluded" entirely and replace with something appropriate
- Ingredients with no feedback: keep unless there is a compelling reason to swap
- Maintain scent balance (base + middle + top note)
- Address the customer directly — they know the product, skip basic explanations
- The summary should acknowledge what they tried and what you've changed

Respond ONLY with valid JSON, no markdown, no preamble:
{
  "summary": "Acknowledge they tried the standard blend. Reference specific changes made based on their feedback. 2-3 sentences. No medical claims.",
  "scent_tags": ["3 short descriptors"],
  "ingredients": [
    {
      "name": "Ingredient Name (Note Type)",
      "reason": "One honest sentence — if this is a swap, explain why this replaces what they disliked",
      "changed": true or false,
      "ml": "e.g. 0.5ml — only if DIY mode"
    }
  ],
  "scent_note": "If scent imbalance or clash detected, note it here. Otherwise null.",
  "upgrade": {
    "ingredient": "Upgrade ingredient name",
    "description": "For Rose Otto: include petal fact. For others: what it adds and why it suits this person.",
    "price": "£XX total"
  },
  "variations": [
    {
      "name": "Short variation name",
      "discount": "15%",
      "description": "What differs"
    }
  ],
  "price": "£XX",
  "price_tier": "Personalised blend · 10ml",
  "subscription_note": "Subscribe and save — £XX/month"
}
`;

    try {
      const res = await fetch(ANTHROPIC_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: KNOWLEDGE,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const data = await res.json();
      const text = (data.content || []).map(b => b.text || "").join("");
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setResult(parsed);
      setPhase("result");
    } catch (e) {
      setError("Something went wrong. Please try again.");
      setPhase("quiz");
      setStep(questions.length - 1);
    }
  };

  const generate = async (upgradeInfo = null) => {
    setPhase("loading");
    setError(null);

    const allFreeText = [
      answers.goal_free, answers.detail_free, answers.scent_free,
      answers.dislikes_free, answers.experience_free
    ].filter(Boolean).join(". ");

    const upgradeNote = upgradeInfo
      ? `UPGRADE REQUESTED: Customer wants to upgrade to include ${upgradeInfo.ingredient}. Rebuild the formula with this premium ingredient as the primary or secondary.`
      : "";

    const prompt = `
Customer quiz answers:
- Goal: ${answers.goal}
- Specific concern: ${answers.detail}
- Age range: ${answers.age}
- Gender: ${answers.gender}
- Scent preferences: ${JSON.stringify(answers.scent)}
- Scents to avoid: ${JSON.stringify(answers.dislikes)}
- Sensitivities: ${JSON.stringify(answers.sensitivities)}
- Experience level: ${answers.experience}
- Free text (interpret carefully): "${allFreeText || "None"}"
- Recipient name: "${answers.recipient_name || "not given"}"
- Gifter name: "${answers.gifter_name || "not a gift"}"
- DIY mode: ${isDIY}
- Vitamin D3 included: ${answers.vitd3 !== false ? "YES — included as standard" : "NO — customer opted out"}
${upgradeNote}

Respond ONLY with valid JSON, no markdown, no preamble:
{
  "summary": "IMPORTANT: If gifter_name is provided AND recipient_name is provided, address the gifter directly: '[GifterName], you've created this blend for [RecipientName] to help her/him with [their specific concern].' If personal use with name, address them: '[Name], this blend was created around your [specific concern].' If no name given, use warm third-person. 2-3 sentences total. No medical claims.",
  "scent_tags": ["3 short descriptors e.g. Grounding", "Floral", "Fresh"],
  "ingredients": [
    {
      "name": "Ingredient Name (Note Type e.g. Base Note)",
      "reason": "One honest sentence referencing their specific quiz answers",
      "ml": "e.g. 0.5ml — only include if DIY mode is true"
    }
  ],
  "scent_note": "If scent imbalance detected or swap suggested, write it here. Otherwise null.",
  "upgrade": {
    "ingredient": "Ingredient Name — this is the headline, be specific e.g. Rose Otto or Rhodiola",
    "description": "Always include: where the ingredient originates (country or region), what it is (plant species, part used, extraction method if relevant), and what it adds to this specific blend. For Rose Otto: approximately 800 rose petals go into the tiny amount we add to a single bottle; steam-distilled from Rosa damascena grown in Bulgaria's Rose Valley; considered one of the most luxurious essential oils in the world. For Rhodiola: grows wild at high altitude in Siberia, Scandinavia, and the Arctic — known as Arctic Root or Golden Root; the root is the active part, traditionally valued for endurance and mental resilience. For Sandalwood: sustainably sourced Santalum album from South India or Australia; a slow-growing tree prized for centuries. For Frankincense: resin tapped from Boswellia trees in Oman, Somalia, and Ethiopia; one of the oldest aromatics in recorded history. For Neroli: steam-distilled from the flowers of the bitter orange tree (Citrus aurantium) in Tunisia and Morocco; famously labour-intensive to harvest. For other ingredients: give honest origin, what it is, what it adds, why it suits this person.",
    "price": "£XX total (not the difference)"
  },
  "variations": [
    {
      "name": "Short variation name",
      "discount": "15%",
      "description": "What differs and why it might suit them differently"
    }
  ],
  "price": "£XX",
  "price_tier": "Standard blend · 10ml",
  "subscription_note": "Subscribe and save — £XX/month"
}

Rules:
- 4-5 ingredients max (Vitamin E always included separately, don't list it)
- Always include base + middle + top note balance
- Respect all sensitivities (pregnant = no clary sage/rosemary; nut allergy = jojoba-only base, note it)
- Female aged 41-55 or 55+: weight towards hormonal support ingredients
- Suggest exactly one upgrade — use Rose Otto as upgrade whenever it fits the goal
- Include 1-2 variations maximum
- Price based on tier rules in knowledge base
- Respond in JSON only, no other text
`;

    try {
      const res = await fetch(ANTHROPIC_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: KNOWLEDGE,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const data = await res.json();
      const text = (data.content || []).map(b => b.text || "").join("");
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setResult(parsed);
      setPhase("result");
    } catch (e) {
      setError("Something went wrong creating your blend. Please try again.");
      setPhase("quiz");
      setStep(QUESTIONS.length - 1);
    }
  };

  const handleUpgrade = (upgradeInfo) => {
    setUpgraded(true);
    generate(upgradeInfo);
  };

  const restart = () => {
    setStep(0); setAnswers({}); setResult(null);
    setPhase("entry"); setUpgraded(false); setIsAmazon(false);
    setExtraDoses({}); setLivePrice(null);
  };

  const startNew = () => { setIsAmazon(false); setPhase("quiz"); setStep(0); };
  const startAmazon = () => { setIsAmazon(true); setPhase("quiz"); setStep(0); };
  const startQR = () => { setPhase("qr"); };

  // Single shared InlineChat instance — passed through quiz steps and shown on result
  const sharedChat = (
    <InlineChat
      answers={answers}
      result={result}
      extraDoses={extraDoses}
      setExtraDoses={setExtraDoses}
      basePrice={result ? (result.price || "32").replace(/[^0-9]/g, "") : "32"}
      setLivePrice={setLivePrice}
    />
  );

  // Render the correct step for Amazon flow
  // steps 0-1 = goal+detail (freetext), steps 2-7 = floating, step 8 = naming (no chat)
  const chatModeForStep = (s) => {
    if (isAmazon) return null;
    if (s === 0) return "freetext";
    if (s <= 7) return "floating";
    return null;
  };

  const renderQuizStep = () => {
    const q = questions[step];
    if (q.type === "amazon_review") {
      return (
        <AmazonReviewStep
          key={step}
          productKey={answers.amazon_product}
          answers={answers}
          onAnswer={handleAnswer}
          onNext={handleNext}
          onBack={() => setStep(s => s - 1)}
        />
      );
    }
    return (
      <QuizStep
        key={step}
        q={q}
        answers={answers}
        onAnswer={handleAnswer}
        onNext={handleNext}
        onBack={() => step === 0 ? setPhase("entry") : setStep(s => s - 1)}
        isFirst={step === 0}
        isDIY={isDIY}
        chatMode={chatModeForStep(step)}
      />
    );
  };

  return (
    <>
      <style>{css}</style>
      <div className="app">
        <div className="grain" />
        <div className="container">
          <header className="site-header">
            <div className="logo-wrap">
              <div style={{display:"flex", alignItems:"baseline", gap:14}}>
                <div className="logo"><span>My</span>Alchemy</div>
                
                <span style={{fontSize:12, letterSpacing:"0.18em", textTransform:"uppercase", color:"var(--gold)", opacity:0.9, fontFamily:"'DM Sans', sans-serif", fontWeight:300, marginLeft:6}}>Demo version</span>
              </div>
              <div className="logo-strapline">Make it personal</div>
              {phase === "entry" && (
                <div className="price-intro-line">Create your own blend · £24 (10ml)</div>
              )}
            </div>
            {phase !== "entry" && (
              <button
                className={`mode-toggle${isDIY ? " active" : ""}`}
                onClick={() => setIsDIY(d => !d)}
              >
                {isDIY ? "✦ DIY Mode On" : "Making it yourself?"}
              </button>
            )}
          </header>

          {phase === "entry" && (
            <EntryScreen onNew={startNew} onAmend={startAmazon} onQR={startQR} />
          )}

          {phase === "qr" && (
            <QRScreen onBack={() => setPhase("entry")} />
          )}

          {phase === "quiz" && (
            <>
              <div className="progress-wrap">
                <div className="progress-meta">
                  <span className="progress-step">{isAmazon ? "Refinement" : "Personalisation"}</span>
                  <span className="progress-fraction">{step + 1} / {totalSteps}</span>
                </div>
                <div className="progress-track">
                  <div className="progress-fill" style={{ width: `${progress}%` }} />
                </div>
              </div>

              {error && <p style={{ color: "var(--gold)", fontSize: 13, marginBottom: 20 }}>{error}</p>}

              {renderQuizStep()}

              {/* Floating support chat for steps 3-8 */}
              {chatModeForStep(step) === "floating" && (
                <FloatingChat answers={answers} />
              )}
            </>
          )}

          {phase === "loading" && <Loading name={answers.recipient_name} />}

          {phase === "result" && result && (
            <Result
              result={result}
              answers={answers}
              isDIY={isDIY}
              onRestart={restart}
              onUpgrade={handleUpgrade}
              upgraded={upgraded}
              livePrice={livePrice}
              inlineChat={sharedChat}
            />
          )}
        </div>
      </div>
    </>
  );
}


